/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef, useContext,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import {
  getMediaLibraryFolderTree,
  getMediaLibraryFolderMoveStatus,
  getMediaLibraryItemList,
  createMediaLibraryFolder,
  renameMediaLibraryFolder,
  deleteMediaLibraryFolder,
  moveMediaLibraryFolder,
  renameMediaLibraryItem,
  deleteMediaLibraryItem,
  batchActionMediaLibraryItem,
  saveMediaLibraryItem,
  isAssetLibraryApiMessageShown,
} from '$/api/canvasRquest';
import type {
  AssetItem,
  MediaLibraryFolderMoveParams,
  MediaLibraryItem,
  MediaLibraryItemListParams,
  MediaLibraryItemSaveParams,
} from '$/api/server.types';
import AssetStorageLimitModal from '$/components/AssetStorageLimitModal';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { downloadFileFromUrl } from '$/utils/export';
import {
  checkAssetStorageBeforeWrite,
  getAssetStorageCheckFailedText,
  getAssetStorageInfo,
  sumMediaLibraryItemSaveFileSize,
} from '$/utils/assetStorage';
import {
  AUDIO_TYPES,
  dealUploadFile,
  FileTypeEnum,
  getVideoCoverUrl,
  IMAGE_TYPES,
  VIDEO_TYPES,
  type CallbackFiles,
} from '$/utils/upload';
import useThemeStore from '$/store/useThemeStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import { openPreview } from '$/store/usePreviewStore';
import { openVipModal, type OpenMode } from '$/store/useVipModalStore';
import useTeamCanvasLockStore from '$/store/useTeamCanvasLockStore';
import { showMediaLibrarySaveModal } from '$/pages/RoboneoTeamFlow/FlowContent/components/SaveToMediaLibraryModal';
import FeedbackModal from '@/components/FeedbackModal';
import { getEnv } from '@/common/agents';
import { getAccessToken } from '@/common/utils/accountUtil';
import { getTeamMembers } from '@/domains/team/teamService';
import type { TeamMemberListItem } from '@/domains/team/types';
import { useTeamPermissions } from '@/domains/team/useTeamPermissions';
import { getGid } from '@/utils';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/Tabs';
import {
  assetDisplayTitle,
  documentVisualVariantKey,
  getDocumentCardVisual,
  isDocumentCardLayout,
} from '$/utils/assetLibraryCardLayout';
import type { WorkflowCanvasMediaItem } from '$/utils/openWorkflowWithMedia';
import MyIpAssetContent from '@/components/AssetModalContent/MyIpAssetContent';
import { useMyIpSession } from '@/hooks/myIp/useMyIpSession';
import { MyIpReturnHostContext } from '$/components/MyIpAssetReturnHost/context';
import { canShowMyIpAssetCategory } from '@/components/AssetModalContent/myIpAssetCategory';
import HistoryModalContent from '../HistoryModalContent';
import OfficialAssetContent from '../OfficialAssetContent';
import darkVoiceArt from '../folderAsset/dark-voice.svg';
import lightVoiceArt from '../folderAsset/light-voice.svg';
import { ASSET_LIBRARY_FOLDER_IMAGES } from '../folderAsset/folderImages';
import styles from '../index.module.scss';
import type {
  MaterialTypeValue,
  AssetItemView,
  AssetGroupView,
  AssetLibraryModalContentProps,
  AssetModalRawItem,
} from '../type';
import AssetStorageCard from '../AssetStorageCard';
import tabStyles from './index.module.scss';
import {
  staticImportedSrc,
  sortFoldersForDropdown,
  isCanvasInsertableMaterialType,
  isWorkflowInsertableMaterialType,
  isBuiltInModalBatchSelectableMaterialType,
  shouldShowAssetInsertHoverBadge,
  buildAssetModalGroupsFromList,
  flattenMediaLibraryFolderTree,
  isAssetModalMaterialFilterMatched,
  mapMediaLibraryItemToModalViewItem,
  trackRoboneoAssetUploadSuccess,
  trackRoboneoAssetLibraryShow,
  trackRoboneoAssetLibraryCardShow,
  trackRoboneoAssetLibraryCardClick,
  trackRoboneoAssetAddCanvasClick,
  assetItemToWorkflowMediaItem,
  insertAssetItemToCanvas,
} from '../utils';
import {
  resolveAssetModalTrackRoomId,
  trackRoboneoAssetBatchBarShow,
  trackRoboneoAssetPopAddClick,
  trackRoboneoAssetRoleSaveResult,
  trackRoboneoAssetRoleSaveUpload,
  trackRoboneoAssetTabClick,
  trackTeamAssetHistorySave,
} from '../tracking';
import {
  AssetModalItemRoot,
  AssetModalConfirmDialog,
  AssetModalMediaCover,
  AssetModalMoveDialog,
  AssetPreviewAddToCanvasSlot,
  type AssetPreviewSaveLocation,
  DEFAULT_PAGE_SIZE,
  type FolderItem,
  MEDIA_LIBRARY_HISTORY_UPLOAD_LIMITS,
  MEDIA_LIBRARY_MAX_NAME_LENGTH,
  type MediaLibraryUploadMaterialType,
  buildAssetDocumentTextPreviewItem,
  buildAssetMediaPreviewList,
  buildMediaLibrarySaveParamsFromHistoryItem,
  fetchRemoteHistoryAssetFileSize,
  getFolderNumericId,
  getFolderParentNumericId,
  getAssetModalDownloadFilename,
  getMediaLibraryAssetFormatLabel,
  getMediaLibraryAssetTitle,
  getMediaLibrarySaveFileExtension,
  isAssetCardClickPreviewable,
  isAssetImagePreviewable,
  isAssetVideoPreviewable,
  normalizeMediaLibrarySaveName,
  normalizePositiveNumber,
  parseMediaLibraryRecordJson,
  resolveAssetViewMediaLibraryMaterialType,
  stringifyMediaLibraryRecord,
} from '../shared/AssetModalShared';
import SubjectPickerBatchFooter from '../shared/SubjectPickerBatchFooter';
import {
  canToggleBatchAssetSelection,
  normalizeMaxSelectableCount,
  toggleBatchAssetSelection,
} from '../shared/batchSelection';
import PersonalAssetImportDialog from './PersonalAssetImportDialog';
import type { AssetLibraryTabItem, AssetLibraryTabKey } from './tabs';
import { useAssetLibraryTab } from './useAssetLibraryTab';
import CreateRoleEntryCard from './CreateRoleModal/CreateRoleEntryCard';
import CreateRoleModal, { type RoleGenEditContext } from './CreateRoleModal';
import type { RoleGenFormValues } from './CreateRoleModal/RoleSettingForm';
import {
  buildRoleGenRoleSnapshot,
  type RoleGenNodeExtra,
} from './CreateRoleModal/roleGenerateTask';
import { buildRoleGenFormSnapshot } from './CreateRoleModal/roleMaterialUtils';
import type { ThreeViewImage } from './CreateRoleModal/useRoleGenSubmit';

const MEDIA_LIBRARY_FOLDER_MOVE_POLL_INTERVAL_MS = 1500;
const MEDIA_LIBRARY_FOLDER_MOVE_POLL_MAX_ATTEMPTS = 40;
const MEDIA_LIBRARY_GEN_ROLE_SYSTEM_KEY = 'gen_role';
const CREATE_ROLE_SAVE_DIALOG_Z_INDEX = 5800;
const MEDIA_LIBRARY_UPLOAD_ACCEPT_TYPES = [...IMAGE_TYPES, ...VIDEO_TYPES, ...AUDIO_TYPES];
const MEDIA_LIBRARY_UPLOAD_ACCEPT = MEDIA_LIBRARY_UPLOAD_ACCEPT_TYPES.join(',');
// 稳定引用：历史记录上传选择器限定的素材类型。用内联数组会在外层弹窗每次 render 时生成新引用，
// 经 allowedMaterialTypeValues → loadAssetList 依赖导致列表反复重新加载。
const MEDIA_LIBRARY_HISTORY_UPLOAD_ALLOWED_TYPES: MaterialTypeValue[] = ['image', 'video', 'audio'];
type AssetStorageFeedbackEnv = 'pre' | 'beta' | 'release';

/** 将当前运行环境转换为反馈系统支持的环境枚举。 */
function getAssetStorageFeedbackEnv(): AssetStorageFeedbackEnv {
  const env = getEnv().ENV;
  if (env === 'beta') return 'beta';
  if (env === 'release' || env === 'production' || env === 'prod') return 'release';
  return 'pre';
}

/** 根据当前资产库弹窗所在页面映射会员订阅弹窗来源。 */
function getAssetStorageVipOpenMode(): OpenMode {
  if (typeof window === 'undefined') return 'other';
  return window.location.pathname.includes('/home') ? 'home_page' : 'other';
}

/** 文件夹异步移动失败错误，用于 catch 时透传服务端状态 message。 */
class MediaLibraryFolderMoveTaskError extends Error {}
interface AssetModalNameDialogProps {
  visible: boolean;
  title: string;
  label: string;
  value: string;
  placeholder?: string;
  submitLabel?: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}
type AssetLibraryDeleteConfirmState =
  | {
    kind: 'folder';
    folder: FolderItem;
  }
  | {
    kind: 'item';
    item: AssetItemView;
  }
  | {
    kind: 'batch';
    itemIds: number[];
  }
  | null;
interface AssetLibraryPanelNavigation {
  activeTab: AssetLibraryTabKey;
  tabs: AssetLibraryTabItem[];
  title: string;
  closeLabel: string;
  onTabChange: (value: AssetLibraryTabKey) => void;
}
interface AssetLibraryPanelShellProps extends AssetLibraryPanelNavigation {
  children: React.ReactNode;
  setOpenAssetPopover?: (open: boolean) => void;
}
interface AssetLibraryPersonalTeamContentProps extends AssetLibraryModalContentProps {
  panelNavigation: AssetLibraryPanelNavigation;
}
interface MediaLibraryFolderMoveReasonLabels {
  cannotMoveToSelf: string;
  cannotMoveToDescendant: string;
  alreadyInTargetDirectory: string;
  duplicateName: string;
  depthLimitExceeded: string;
}
/** 获取团队成员的可读名称，昵称为空时依次回退手机号、邮箱和 UID。 */
function getTeamMemberDisplayName(member: TeamMemberListItem): string {
  return member.screen_name?.trim()
    || member.phone?.trim()
    || member.email?.trim()
    || String(member.uid);
}
/** 渲染团队成员头像；头像不可用时保留与历史记录一致的圆形渐变占位。 */
function TeamMemberAvatar({ member }: { member: TeamMemberListItem; }): React.ReactElement {
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setLoadFailed(false);
  }, [member.avatar]);

  if (!member.avatar || loadFailed) {
    return <span className={styles.teamMemberAvatar} aria-hidden />;
  }

  return (
    <span className={styles.teamMemberAvatar} aria-hidden>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        className={styles.teamMemberAvatarImage}
        src={member.avatar}
        alt=""
        crossOrigin="anonymous"
        onError={() => setLoadFailed(true)}
      />
    </span>
  );
}
/**
 * 从弹窗视图项中读取资产库 item_id。
 */
function getMediaLibraryItemNumericId(item?: AssetItemView | null): number | undefined {
  const raw = item?.raw;
  const value = raw?.mediaLibraryItemId ?? raw?.media_library_item_id ?? raw?.id ?? item?.id;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}
/**
 * 统一的 20 字命名弹窗，用于新素材库文件夹和素材重命名。
 */
function AssetModalNameDialog({
  visible,
  title,
  label,
  value,
  placeholder,
  submitLabel,
  onChange,
  onClose,
  onSubmit,
}: AssetModalNameDialogProps) {
  const languageStore = useLanguageStore((s) => s.languageStore);
  useEffect(() => {
    if (!visible) return undefined;
    /** Esc 在弹窗内任意焦点位置都按取消处理，避免只有输入框聚焦时才生效。 */
    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [onClose, visible]);
  if (!visible) return null;
  const canSubmit = value.trim().length > 0 && Array.from(value).length <= MEDIA_LIBRARY_MAX_NAME_LENGTH;
  const inputId = `asset-modal-name-${title}`;
  const confirmLabel = submitLabel || languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm);
  return (
    <div className={styles.manageDialogOverlay} role="dialog" aria-modal="true">
      <button
        type="button"
        className={styles.manageDialogBackdrop}
        onClick={onClose}
        aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
        tabIndex={-1}
      />
      <div className={cn(styles.manageDialog, styles.nameDialog)}>
        <div className={styles.manageDialogHeader}>
          <h2 className={styles.manageDialogTitle}>{title}</h2>
          <button
            type="button"
            className={styles.manageDialogClose}
            onClick={onClose}
            aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
          >
            <IconFont name="crossBlack" fontSize={20} color="currentColor" />
          </button>
        </div>
        <label className={styles.manageDialogField} htmlFor={inputId}>
          <span className={styles.manageDialogLabel}>{label}</span>
          <div className={styles.manageDialogInputWrap}>
            <input
              id={inputId}
              type="text"
              className={styles.manageDialogInput}
              placeholder={placeholder}
              value={value}
              maxLength={MEDIA_LIBRARY_MAX_NAME_LENGTH}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) {
                  e.preventDefault();
                  onSubmit();
                }
                if (e.key === 'Escape') {
                  onClose();
                }
              }}
            />
            <span className={styles.manageDialogCount}>
              {Array.from(value).length}
              /
              {MEDIA_LIBRARY_MAX_NAME_LENGTH}
            </span>
          </div>
        </label>
        <div className={styles.manageDialogFooter}>
          <button type="button" className={styles.manageDialogCancel} onClick={onClose}>
            {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_cancel)}
          </button>
          <button type="button" className={styles.manageDialogSubmit} disabled={!canSubmit} onClick={onSubmit}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
/**
 * 判断资产库条目是否满足专用入口的素材类型限制，文本/脚本按文件类限制处理。
 */
function isMediaLibraryItemAllowedByExplicitTypes(item: AssetItemView, allowedValues: MaterialTypeValue[]): boolean {
  if (allowedValues.length === 0) return true;
  return allowedValues.some((value) => isAssetModalMaterialFilterMatched(item, value));
}
/**
 * 将资产库分页行转换、筛选并排序，供时间分组与批量选择共用同一份可见数据。
 */
function buildMediaLibraryModalRows(
  rows: MediaLibraryItem[],
  materialType: MaterialTypeValue,
  allowedValues: MaterialTypeValue[],
  sortOrder: 'asc' | 'desc',
): {
    rawRows: AssetItem[];
    items: AssetItemView[];
  } {
  const pairs = rows
    .map((row) => {
      const item = mapMediaLibraryItemToModalViewItem(row);
      return { raw: item.raw, item };
    })
    .filter(({ item }) => isAssetModalMaterialFilterMatched(item, materialType))
    .filter(({ item }) => isMediaLibraryItemAllowedByExplicitTypes(item, allowedValues));
  pairs.sort((a, b) => (sortOrder === 'desc' ? b.raw.created_at - a.raw.created_at : a.raw.created_at - b.raw.created_at));
  return {
    rawRows: pairs.map((pair) => pair.raw),
    items: pairs.map((pair) => pair.item),
  };
}
interface MediaLibraryFolderGridCardProps {
  folder: FolderItem;
  disabled: boolean;
  hideActionMenu?: boolean;
  onOpen: (folderId: number) => void;
  onActionMenu: (folder: FolderItem, anchorEl: HTMLElement) => void;
}
/**
 * 判断文件夹是否为生成角色系统固定分类，固定分类不展示文件夹管理入口。
 */
function isGenRoleMediaLibraryFolder(folder?: FolderItem | null): boolean {
  return folder?.systemKey === MEDIA_LIBRARY_GEN_ROLE_SYSTEM_KEY;
}
/**
 * 读取文件夹 sort_order；缺省时返回无穷大，让接口返回顺序作为兜底稳定顺序。
 */
function getFolderSortOrder(folder?: FolderItem | null): number {
  const sortOrder = Number(folder?.sortOrder);
  return Number.isFinite(sortOrder) && sortOrder > 0 ? sortOrder : Number.MAX_SAFE_INTEGER;
}
/**
 * 构造刚创建文件夹的本地兜底数据，避免接口刷新延迟时选中态被重置到旧文件夹。
 */
function buildOptimisticMediaLibraryFolder(
  folders: FolderItem[],
  parentId: number,
  folderId: number,
  name: string,
  sortOrder = 1,
): FolderItem {
  const parentFolder = folders.find((folder) => getFolderNumericId(folder) === parentId);
  return {
    id: String(folderId),
    name,
    isPinned: false,
    pinnedAt: 0,
    depth: parentFolder ? (Number(parentFolder.depth) || 1) + 1 : 1,
    parentId: String(parentId),
    sortOrder,
    hasChildren: false,
    folderKind: 'user',
    assetCount: 0,
  };
}
/**
 * 计算同父级中普通文件夹可进入的最小排序位，避免越过生成角色固定分类。
 */
function getFolderMinimumSortOrderAfterGenRoleFolders(
  folders: FolderItem[],
  parentId: number,
  movingFolderId?: number,
): number {
  const siblings = getFoldersByParentDisplayOrder(folders, parentId).filter(
    (folder) => getFolderNumericId(folder) !== movingFolderId,
  );
  const lastFixedIndex = siblings.reduce((index, folder, currentIndex) => (
    isGenRoleMediaLibraryFolder(folder) ? currentIndex : index
  ), -1);
  return lastFixedIndex >= 0 ? lastFixedIndex + 2 : 1;
}
/**
 * 将本地兜底文件夹插入到同父级固定分类后的第一位，匹配“新建文件夹最新在最上”的交互要求。
 */
function insertOptimisticMediaLibraryFolder(folders: FolderItem[], folder: FolderItem): FolderItem[] {
  const folderId = getFolderNumericId(folder);
  const parentId = getFolderParentNumericId(folder);
  const nextFolders = folders.filter((item) => getFolderNumericId(item) !== folderId);
  const fixedSiblingIndex = nextFolders.reduce((lastIndex, item, index) => (
    getFolderParentNumericId(item) === parentId && isGenRoleMediaLibraryFolder(item) ? index : lastIndex
  ), -1);
  if (fixedSiblingIndex >= 0) {
    return [...nextFolders.slice(0, fixedSiblingIndex + 1), folder, ...nextFolders.slice(fixedSiblingIndex + 1)];
  }
  const siblingIndex = nextFolders.findIndex((item) => getFolderParentNumericId(item) === parentId);
  if (siblingIndex < 0) {
    return [folder, ...nextFolders];
  }
  return [...nextFolders.slice(0, siblingIndex), folder, ...nextFolders.slice(siblingIndex)];
}
/**
 * 等待指定毫秒数，供文件夹移动异步任务轮询节流使用。
 */
function delayMediaLibraryFolderMovePoll(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}
/**
 * 轮询文件夹移动异步任务，直到服务端确认 done 或 failed。
 */
async function waitForMediaLibraryFolderMoveTask(taskId: string, attempt = 0): Promise<void> {
  if (attempt >= MEDIA_LIBRARY_FOLDER_MOVE_POLL_MAX_ATTEMPTS) {
    throw new MediaLibraryFolderMoveTaskError(
      'folder_move_operation_in_progress_please_refresh_later_to_view',
    );
  }
  const result = await getMediaLibraryFolderMoveStatus(taskId);
  if (result.status === 'done') return;
  if (result.status === 'failed') {
    throw new MediaLibraryFolderMoveTaskError(result.message || 'folder_move_failed');
  }
  await delayMediaLibraryFolderMovePoll(MEDIA_LIBRARY_FOLDER_MOVE_POLL_INTERVAL_MS);
  await waitForMediaLibraryFolderMoveTask(taskId, attempt + 1);
}
/**
 * 计算文件夹子树的最大层级，用于判断移动后是否会超过 10 级限制。
 */
function getFolderSubtreeMaxDepth(folders: FolderItem[], rootFolder: FolderItem): number {
  const rootId = getFolderNumericId(rootFolder);
  if (!rootId) return Number(rootFolder.depth) || 1;
  const foldersById = new Map(folders.map((folder) => [getFolderNumericId(folder), folder]));
  let maxDepth = Number(rootFolder.depth) || 1;
  folders.forEach((folder) => {
    let parentId = getFolderParentNumericId(folder);
    const visited = new Set<number>();
    while (parentId > 0 && !visited.has(parentId)) {
      if (parentId === rootId) {
        maxDepth = Math.max(maxDepth, Number(folder.depth) || maxDepth);
        break;
      }
      visited.add(parentId);
      const parent = foldersById.get(parentId);
      parentId = getFolderParentNumericId(parent);
    }
  });
  return maxDepth;
}
/**
 * 判断候选目标是否位于待移动文件夹子树内，避免把文件夹移动到自身或子级下。
 */
function isFolderInSubtree(folders: FolderItem[], targetFolder: FolderItem, rootFolder: FolderItem): boolean {
  const rootId = getFolderNumericId(rootFolder);
  const targetId = getFolderNumericId(targetFolder);
  if (!rootId || !targetId) return false;
  if (targetId === rootId) return true;
  const foldersById = new Map(folders.map((folder) => [getFolderNumericId(folder), folder]));
  let parentId = getFolderParentNumericId(targetFolder);
  const visited = new Set<number>();
  while (parentId > 0 && !visited.has(parentId)) {
    if (parentId === rootId) return true;
    visited.add(parentId);
    const parent = foldersById.get(parentId);
    parentId = getFolderParentNumericId(parent);
  }
  return false;
}
/**
 * 判断目标目录下是否已有同名文件夹；当前文件夹在原父级下移动时排除自身。
 */
function hasDuplicateFolderNameInTarget(
  folders: FolderItem[],
  movingFolder: FolderItem,
  targetParentId: number,
): boolean {
  const movingFolderId = getFolderNumericId(movingFolder);
  return folders.some((folder) => {
    if (getFolderNumericId(folder) === movingFolderId) return false;
    return getFolderParentNumericId(folder) === targetParentId && folder.name.trim() === movingFolder.name.trim();
  });
}
/**
 * 计算移动弹窗里每个目标文件夹不可选的原因，原因文案同时用于 hover title 和点击提示。
 */
function buildFolderMoveDisabledReasons(
  folders: FolderItem[],
  movingFolder: FolderItem | null | undefined,
  labels: MediaLibraryFolderMoveReasonLabels,
): Map<number, string> {
  const reasons = new Map<number, string>();
  if (!movingFolder) return reasons;
  const reasonLabels = labels;
  const movingDepth = Number(movingFolder.depth) || 1;
  const currentParentId = getFolderParentNumericId(movingFolder);
  const subtreeMaxDepth = getFolderSubtreeMaxDepth(folders, movingFolder);
  const subtreeDepth = subtreeMaxDepth - movingDepth + 1;
  folders.forEach((targetFolder) => {
    const targetId = getFolderNumericId(targetFolder);
    if (!targetId) return;
    if (isFolderInSubtree(folders, targetFolder, movingFolder)) {
      reasons.set(
        targetId,
        targetId === getFolderNumericId(movingFolder)
          ? reasonLabels.cannotMoveToSelf
          : reasonLabels.cannotMoveToDescendant,
      );
      return;
    }
    if (targetId === currentParentId) {
      reasons.set(targetId, reasonLabels.alreadyInTargetDirectory);
      return;
    }
    if (movingDepth === 10) {
      reasons.set(targetId, reasonLabels.depthLimitExceeded);
      return;
    }
    if (hasDuplicateFolderNameInTarget(folders, movingFolder, targetId)) {
      reasons.set(targetId, reasonLabels.duplicateName);
      return;
    }
    const targetDepth = Number(targetFolder.depth) || 1;
    if (targetDepth + subtreeDepth > 10) {
      reasons.set(targetId, reasonLabels.depthLimitExceeded);
    }
  });
  return reasons;
}
/**
 * 读取同父级文件夹的展示顺序，优先使用 sort_order，缺省时按当前树扁平列表顺序兜底。
 */
function getFoldersByParentDisplayOrder(folders: FolderItem[], parentId: number): FolderItem[] {
  const indexById = new Map(folders.map((folder, index) => [getFolderNumericId(folder) ?? Number(folder.id), index]));
  return folders
    .filter((folder) => getFolderParentNumericId(folder) === parentId)
    .sort((a, b) => {
      const bySortOrder = getFolderSortOrder(a) - getFolderSortOrder(b);
      if (bySortOrder !== 0) return bySortOrder;
      return (
        (indexById.get(getFolderNumericId(a) ?? Number(a.id)) ?? 0)
        - (indexById.get(getFolderNumericId(b) ?? Number(b.id)) ?? 0)
      );
    });
}
/**
 * 计算移动到目标父级后的默认落位；“移动到”弹窗不指定缝隙时排到目标目录末尾。
 */
function getFolderMoveDefaultSortOrder(folders: FolderItem[], targetParentId: number, movingFolderId: number): number {
  const childCount = getFoldersByParentDisplayOrder(folders, targetParentId).filter(
    (folder) => getFolderNumericId(folder) !== movingFolderId,
  ).length;
  return childCount + 1;
}
/**
 * 计算文件夹在当前父级下是否可继续上移或下移。
 */
function getFolderSiblingMoveState(
  folders: FolderItem[],
  folder?: FolderItem | null,
): {
    canMoveUp: boolean;
    canMoveDown: boolean;
  } {
  const folderId = getFolderNumericId(folder);
  if (!folderId) return { canMoveUp: false, canMoveDown: false };
  const siblings = getFoldersByParentDisplayOrder(folders, getFolderParentNumericId(folder));
  const index = siblings.findIndex((item) => getFolderNumericId(item) === folderId);
  const previousFolder = index > 0 ? siblings[index - 1] : undefined;
  return {
    canMoveUp: index > 0 && !isGenRoleMediaLibraryFolder(previousFolder),
    canMoveDown: index >= 0 && index < siblings.length - 1,
  };
}
/**
 * 计算文件夹同级调序后的 1-based 目标位置。
 */
function getFolderSiblingMoveSortOrder(
  folders: FolderItem[],
  folder: FolderItem,
  direction: 'up' | 'down',
): number | undefined {
  const folderId = getFolderNumericId(folder);
  if (!folderId) return undefined;
  const siblings = getFoldersByParentDisplayOrder(folders, getFolderParentNumericId(folder));
  const index = siblings.findIndex((item) => getFolderNumericId(item) === folderId);
  if (direction === 'up') {
    const previousFolder = index > 0 ? siblings[index - 1] : undefined;
    return index > 0 && !isGenRoleMediaLibraryFolder(previousFolder) ? index : undefined;
  }
  return index >= 0 && index < siblings.length - 1 ? index + 2 : undefined;
}
/**
 * 右侧内容区文件夹卡片，匹配 Figma 固定 140×182 卡片规格。
 */
function MediaLibraryFolderGridCard({
  folder,
  disabled,
  hideActionMenu,
  onOpen,
  onActionMenu,
}: MediaLibraryFolderGridCardProps): React.ReactElement {
  const languageStore = useLanguageStore((s) => s.languageStore);
  const folderId = getFolderNumericId(folder);
  const showActionMenu = !disabled && !hideActionMenu;
  const assetCountText = languageStore
    .smartcatTranslationText((v) => v.asset_count_unit)
    .replace('{number}', String(folder.assetCount ?? 0));
  return (
    <div
      className={cn(styles.mediaLibraryFolderCard, {
        [styles.mediaLibraryFolderCardDisabled]: disabled,
      })}
      role="button"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      title={folder.name}
      onClick={() => {
        if (!disabled && folderId != null) onOpen(folderId);
      }}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ') && folderId != null) {
          e.preventDefault();
          onOpen(folderId);
        }
      }}
    >
      {showActionMenu && (
        <button
          type="button"
          className={styles.mediaLibraryFolderCardMoreBtn}
          aria-label={languageStore.smartcatTranslationText((v) => v.roboneo_web_more)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onActionMenu(folder, e.currentTarget);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              onActionMenu(folder, e.currentTarget);
            }
          }}
        >
          <IconFont name="ellipsisFill" fontSize={14} color="#fff" />
        </button>
      )}
      <div className={styles.mediaLibraryFolderCardPreview} aria-hidden />
      <div className={styles.mediaLibraryCardText}>
        <div className={styles.mediaLibraryCardTitle}>{folder.name}</div>
        <div className={styles.mediaLibraryCardMeta}>
          <span>{assetCountText}</span>
        </div>
      </div>
    </div>
  );
}
/**
 * 素材库卡片副信息左侧文案：音频按 Figma 展示为“音色”，其他优先显示当前文件夹语义。
 */
function getMediaLibraryAssetTypeLabel(
  item: AssetItemView,
  selectedFolderName: string,
  labels: Record<'audio' | 'video' | 'script' | 'text' | 'document' | 'image', string>,
): string {
  if (item.materialType === 'audio') return labels.audio;
  if (item.materialType === 'video') return labels.video;
  if (item.materialType === 'script') return labels.script;
  if (item.materialType === 'text') return labels.text;
  if (item.materialType === 'document') return labels.document;
  return selectedFolderName || labels.image;
}
/**
 * 将上传 SDK 的文件类型映射为资产库保存接口支持的素材类型。
 */
function resolveUploadedMediaLibraryMaterialType(file: CallbackFiles): MediaLibraryUploadMaterialType | undefined {
  if (file.type === FileTypeEnum.Image) return 'image';
  if (file.type === FileTypeEnum.Video) return 'video';
  if (file.type === FileTypeEnum.Audio) return 'audio';
  return undefined;
}
/**
 * 本地上传完成后，构造保存到当前资产库文件夹的接口参数。
 */
function buildMediaLibrarySaveParamsFromUploadedFile(
  file: CallbackFiles,
  folderId: number,
): MediaLibraryItemSaveParams | null {
  const materialType = resolveUploadedMediaLibraryMaterialType(file);
  const url = file.url?.trim();
  if (!materialType || !url) return null;
  const fileExtension = (file.suffix || getMediaLibrarySaveFileExtension(url, file.name)).toLowerCase();
  const thumbnailUrl = materialType === 'image' ? url : file.coverUrl || (materialType === 'video' ? getVideoCoverUrl(url) : undefined);
  return {
    folder_id: folderId,
    name: normalizeMediaLibrarySaveName(file.name, `${materialType}-${Date.now()}`),
    material_type: materialType,
    url,
    thumbnail_url: thumbnailUrl || undefined,
    file_size: normalizePositiveNumber(file.size),
    ext: stringifyMediaLibraryRecord({
      file_extension: fileExtension,
      width: normalizePositiveNumber(file.width),
      height: normalizePositiveNumber(file.height),
      source_from: 'upload',
      content_source: 'upload',
    }),
  };
}
/** 从新保存的角色素材中读取重新编辑上下文，缺少真实任务 id 时不开放入口。 */
function buildRoleGenEditContext(item: AssetItemView): RoleGenEditContext | null {
  const genParams = parseMediaLibraryRecordJson(item.raw.gen_params);
  const taskId = genParams.task_id as string | undefined;
  const roleGen = genParams.role_gen as RoleGenNodeExtra['role_gen'] | undefined;
  if (!taskId || !roleGen) return null;
  return {
    taskId,
    formValues: buildRoleGenFormSnapshot({ role_gen: roleGen }),
  };
}

/** 将创建角色结果图转换为新资产库保存参数，保留重新编辑所需的任务和表单数据。 */
async function buildMediaLibrarySaveParamsFromRoleGenImage(
  image: ThreeViewImage,
  formSnapshot: RoleGenFormValues,
  roleTaskId: string,
  folderId: number,
  assetName: string,
): Promise<MediaLibraryItemSaveParams | null> {
  const url = image.url.trim();
  if (!url) return null;
  const roleName = formSnapshot.name.trim();
  const roleGenSnapshot = buildRoleGenRoleSnapshot(formSnapshot);
  const fileExtension = getMediaLibrarySaveFileExtension(url, assetName) || 'jpg';
  const fileSize = await fetchRemoteHistoryAssetFileSize(url);
  return {
    folder_id: folderId,
    name: normalizeMediaLibrarySaveName(assetName, `role-${Date.now()}`),
    material_type: 'image',
    url,
    thumbnail_url: url,
    file_size: fileSize,
    gen_params: stringifyMediaLibraryRecord({
      task_id: roleTaskId,
      role_gen: {
        ...roleGenSnapshot,
        name: roleName,
      },
    }),
    ext: stringifyMediaLibraryRecord({
      file_extension: fileExtension,
      source_from: 'gen_role',
      content_source: 'gen_role',
    }),
  };
}

type RoleGenCanvasItem = AssetItemView & {
  assetId: string;
  source: 'generation';
};

/**
 * 将角色结果图适配为资产库统一画布插入项，不伪造后端资产 id。
 */
function buildRoleGenCanvasItem(
  image: ThreeViewImage,
  formSnapshot: RoleGenFormValues,
): RoleGenCanvasItem | null {
  const url = image.url.trim();
  if (!url) return null;
  const generatedId = `gen-role-${Date.now()}`;
  const fileExtension = getMediaLibrarySaveFileExtension(url, formSnapshot.name) || 'png';
  const raw = {
    id: 0,
    name: formSnapshot.name.trim() || '角色',
    task_type: 'canvas',
    tool_type: 'gen_role',
    material_type: 'image',
    source_type: 'canvas',
    source_id: generatedId,
    url,
    thumbnail_url: url,
    is_favorited: 0,
    created_at: Date.now(),
  } as AssetModalRawItem;
  return {
    id: generatedId,
    coverUrl: url,
    mediaUrl: url,
    materialType: 'image',
    aspectRatio: 1,
    fileExtension,
    cardVariant: 'image',
    raw,
    // TeamFlow 优先读取顶层 assetId，非数字值避免把前端临时 id 当成持久资产 id。
    assetId: generatedId,
    source: 'generation',
  };
}
/**
 * 文档类素材在素材库网格中的 132×132 预览块。
 */
function MediaLibraryDocumentPreview({ item }: { item: AssetItemView; }): React.ReactElement {
  if (item.materialType === 'script' || item.materialType === 'text') {
    return (
      <div className={styles.mediaLibraryDocumentPreviewSimple} aria-hidden>
        <IconFont
          name={item.materialType === 'script' ? 'tableBold' : 'textAlignmentLeftBold'}
          fontSize={36}
          color="var(--content-tertiary, rgb(247 248 250 / 42%))"
        />
      </div>
    );
  }
  const visual = getDocumentCardVisual(documentVisualVariantKey(item.cardVariant, item.materialType));
  return (
    <div className={styles.mediaLibraryDocumentPreview} aria-hidden>
      <div className={styles.mediaLibraryDocumentIcon} style={{ background: visual.iconBg }}>
        <IconFont name={visual.icon} fontSize={24} color="#fff" />
      </div>
      <span>{visual.typeLabel}</span>
    </div>
  );
}
/** 资产库外框，承载顶层入口 tab 与关闭按钮，内容区由个人/团队和官方资产分别挂载。 */
function AssetLibraryPanelShell({
  activeTab,
  tabs,
  title,
  closeLabel,
  onTabChange,
  setOpenAssetPopover,
  children,
}: AssetLibraryPanelShellProps): React.ReactElement {
  /** 切换资产库顶层 tab 时上报点击，并继续交给外层持久化最后访问 tab。 */
  const handleTabChange = useCallback((value: string): void => {
    const nextTab = value as AssetLibraryTabKey;
    if (nextTab !== activeTab) {
      trackRoboneoAssetTabClick(nextTab);
    }
    onTabChange(nextTab);
  }, [activeTab, onTabChange]);

  return (
    <div className={cn(styles.panel, styles.mediaLibraryPanel)} data-asset-library>
      <div className={cn(styles.panelHeader, tabStyles.assetLibraryHeader)}>
        <div className={tabStyles.assetLibraryTitleWrap}>
          <span className={tabStyles.assetLibraryTitle}>{title}</span>
        </div>
        <Tabs
          className={tabStyles.assetLibraryTabs}
          value={activeTab}
          onValueChange={handleTabChange}
        >
          <TabsList className={tabStyles.assetLibraryTabList}>
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.key}
                className={tabStyles.assetLibraryTabTrigger}
                disabled={tab.disabled}
                value={tab.key}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <button
          type="button"
          className={cn(styles.panelCloseBtn, tabStyles.assetLibraryCloseBtn)}
          aria-label={closeLabel}
          onClick={() => setOpenAssetPopover?.(false)}
        >
          <IconFont name="crossBlack" fontSize={16} color="var(--content-primary, #fff)" />
        </button>
      </div>
      {children}
    </div>
  );
}
/** 个人/团队资产库主体，复用现有列表、文件夹、上传和插入逻辑。 */
function AssetLibraryPersonalTeamContent({
  enableIpCategory: enableIpCategoryProp,
  browseIpCategory,
  ipCategoryContent,
  setOpenAssetPopover,
  pickerSource,
  maxSelectableCount,
  insertTarget = 'canvas',
  trackLocation,
  enableUploadSuccessTrack = true,
  enableAddCanvasClickTrack = true,
  closeOnBatchConfirm = true,
  defaultBatchMode = false,
  onInsertWorkflowMedia,
  onAssetItemClick,
  onAssetBatchItemClick,
  onAssetBatchConfirm,
  batchConfirmLabel,
  assetActionLabel,
  allowedMaterialTypes,
  onIpItemsAdd,
  maxIpSelectableCount,
  ipAddLabel,
  hideIpMaterials,
  hideIpDetail,
  panelNavigation,
}: AssetLibraryPersonalTeamContentProps) {
  const enableIpCategory = canShowMyIpAssetCategory({
    enableIpCategory: enableIpCategoryProp,
    browseIpCategory,
    ipCategoryContent,
    pickerSource,
    allowedMaterialTypes,
    onAssetItemClick,
    onAssetBatchConfirm,
    onAssetBatchItemClick,
  });
  const [ipSelected, setIpSelected] = useState(enableIpCategory);
  /**
   * 画布 / Agent 团队 / 工作流等内置插入：素材类型筛选与资产页一致展示全部枚举；批量勾选仍仅限可插入类型。
   * 父组件托管点击/批量（如团队对话框）时不启用。
   */
  const limitBuiltInBatchMedia = !onAssetItemClick
    && !onAssetBatchConfirm
    && (insertTarget === 'canvas' || insertTarget === 'agentTeam' || insertTarget === 'workflow');
  // 只有主体库来源启用外部选择上限，普通资产库继续沿用原批量处理能力。
  const subjectPickerMaxSelectableCount = pickerSource === 'subjectLibrary'
    ? normalizeMaxSelectableCount(maxSelectableCount)
    : undefined;
  const isSubjectLibraryPicker = pickerSource === 'subjectLibrary'
    && subjectPickerMaxSelectableCount != null;
  const languageStore = useLanguageStore((s) => s.languageStore);
  const userInfo = useOfficialStore((state) => state.userInfo);
  const loadSubscribeInfo = useOfficialStore((state) => state.loadSubscribeInfo);
  const theme = useThemeStore((s) => s.theme);
  const teamOid = useOfficialStore((s) => s.clientOId);
  const currentUserUid = useOfficialStore((s) => s.userInfo.uid);
  const teamCanvasLockRole = useTeamCanvasLockStore((state) => state.role);
  const isTeamAssetLibrary = teamOid != null && teamOid > 0;
  const teamCanvasOperationForbidden = insertTarget === 'agentTeam'
    && isTeamAssetLibrary
    && teamCanvasLockRole !== 'idle'
    && teamCanvasLockRole !== 'holder';
  const createRoleAddToCanvasDisabledReason = teamCanvasOperationForbidden
    ? languageStore.smartcatTranslationText(
      (v) => v.you_currently_have_no_operation_permissions_please_contact_the_administrator_for_an_update_,
    )
    : '';
  const {
    canDelete,
    canDownload,
  } = useTeamPermissions();
  const permissionDeniedText = languageStore.smartcatTranslationText(
    (value) => value.you_currently_have_no_operation_permissions_please_contact_the_administrator_for_an_update_,
  );
  const batchProcessingText = languageStore.smartcatTranslationText(
    (value) => value.batched_processing,
  );
  const filterByMemberText = languageStore.smartcatTranslationText(
    (value) => value.filter_by_member,
  );
  const importFromPersonalAssetsText = languageStore.smartcatTranslationText(
    (value) => value.import_from_personal_assets,
  );
  const teamMembersText = languageStore.smartcatTranslationText(
    (value) => value.team_members,
  );
  const noTeamMembersText = languageStore.smartcatTranslationText(
    (value) => value.no_team_members_available,
  );
  const currentUserSuffixText = languageStore.smartcatTranslationText((value) => value._i_);
  const unnamedFolderText = languageStore.smartcatTranslationText((value) => value.unnamed_folder);
  const generatedCharacterText = languageStore.smartcatTranslationText(
    (value) => value.generate_character,
  );
  const folderNameTooLongText = languageStore
    .smartcatTranslationText((value) => value.the_folder_name_cannot_exceed_num_characters_)
    .replace('{num}', String(MEDIA_LIBRARY_MAX_NAME_LENGTH));
  const assetNameTooLongText = languageStore
    .smartcatTranslationText((value) => value.the_asset_name_cannot_exceed_num_characters_)
    .replace('{num}', String(MEDIA_LIBRARY_MAX_NAME_LENGTH));
  const voiceArtSrc = useMemo(() => staticImportedSrc(theme === 'light' ? lightVoiceArt : darkVoiceArt), [theme]);
  const allowedMaterialTypeValues = useMemo(() => {
    return Array.from(new Set(allowedMaterialTypes?.filter((value) => value !== 'all') ?? []));
  }, [allowedMaterialTypes]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined);
  const [materialType, setMaterialType] = useState<MaterialTypeValue>(allowedMaterialTypeValues[0] ?? 'all');
  const sortOrder = 'desc';
  const listGroupMode = 'time';
  const [groups, setGroups] = useState<AssetGroupView[]>([]);
  const [loading, setLoading] = useState(false);
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMemberListItem[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersLoadFailed, setTeamMembersLoadFailed] = useState(false);
  const [selectedCreatedByUid, setSelectedCreatedByUid] = useState<string | undefined>(undefined);
  const [batchMode, setBatchMode] = useState(defaultBatchMode);
  const [mediaLibraryManageBatchMode, setMediaLibraryManageBatchMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [folderNameDialogOpen, setFolderNameDialogOpen] = useState(false);
  const [folderNameDraft, setFolderNameDraft] = useState('');
  const [folderNameTarget, setFolderNameTarget] = useState<FolderItem | null>(null);
  const [folderCreateParentId, setFolderCreateParentId] = useState<number>(0);
  const [itemNameDialogOpen, setItemNameDialogOpen] = useState(false);
  const [itemNameDraft, setItemNameDraft] = useState('');
  const [itemNameTarget, setItemNameTarget] = useState<AssetItemView | null>(null);
  const [deleteConfirmState, setDeleteConfirmState] = useState<AssetLibraryDeleteConfirmState>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveItemIds, setMoveItemIds] = useState<number[]>([]);
  const [moveFolderTarget, setMoveFolderTarget] = useState<FolderItem | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<number | undefined>(undefined);
  const [itemActionMenu, setItemActionMenu] = useState<{
    item: AssetItemView;
    left: number;
    top: number;
  } | null>(null);
  const [folderActionMenu, setFolderActionMenu] = useState<{
    folder: FolderItem;
    left: number;
    top: number;
  } | null>(null);
  const [mediaLibraryUploadMenuOpen, setMediaLibraryUploadMenuOpen] = useState(false);
  const [mediaLibraryUploading, setMediaLibraryUploading] = useState(false);
  const [createRoleModalOpen, setCreateRoleModalOpen] = useState(false);
  const [createRoleEditContext, setCreateRoleEditContext] = useState<RoleGenEditContext | null>(null);
  const [assetStorageLimitModalOpen, setAssetStorageLimitModalOpen] = useState(false);
  const [assetStorageFeedbackOpen, setAssetStorageFeedbackOpen] = useState(false);
  const [historyUploadPickerOpen, setHistoryUploadPickerOpen] = useState(false);
  const [personalAssetImportOpen, setPersonalAssetImportOpen] = useState(false);
  const memberDropdownRef = useRef<HTMLDivElement>(null);
  const itemActionMenuRef = useRef<HTMLDivElement>(null);
  const folderActionMenuRef = useRef<HTMLDivElement>(null);
  const mediaLibraryUploadMenuRef = useRef<HTMLDivElement>(null);
  const mediaLibraryUploadInputRef = useRef<HTMLInputElement>(null);
  const createRoleSaveDialogOpenRef = useRef(false);
  const previousMediaLibraryManageBatchModeForTrackRef = useRef(false);
  /** 资产库容量 CTA：最高等级会员进入反馈，其他用户打开会员订阅弹窗。 */
  const handleAssetStorageAction = useCallback(() => {
    const info = getAssetStorageInfo(useOfficialStore.getState().userInfo.subscribe);
    if (info.isHighestLevel) {
      setAssetStorageFeedbackOpen(true);
      return;
    }
    // 会员订阅弹窗由全局层渲染，先关闭资产库弹窗，避免被当前弹窗层级遮住。
    setOpenAssetPopover?.(false);
    window.setTimeout(() => {
      openVipModal(getAssetStorageVipOpenMode());
    }, 0);
  }, [setOpenAssetPopover]);
  /** 写入资产库前校验容量；容量或素材大小未知时按既有产品规则中断写入。 */
  const validateAssetStorageBeforeWrite = useCallback((incomingBytes?: number): boolean => {
    const checkResult = checkAssetStorageBeforeWrite({
      incomingBytes,
      subscribe: useOfficialStore.getState().userInfo.subscribe,
    });
    if (checkResult.status === 'pass') return true;
    if (checkResult.status === 'unavailable') {
      message.error(getAssetStorageCheckFailedText());
      return false;
    }
    setAssetStorageLimitModalOpen(true);
    return false;
  }, []);
  /** 空间不足弹窗 CTA 会先关闭当前弹窗，再进入升级或反馈链路。 */
  const handleAssetStorageLimitAction = useCallback(() => {
    setAssetStorageLimitModalOpen(false);
    handleAssetStorageAction();
  }, [handleAssetStorageAction]);
  /** 通用资产库选择成功埋点；发布弹窗等宿主自带专用参数时跳过这里的兜底上报。 */
  const trackAssetUploadSuccess = useCallback(
    (items: AssetItemView[]) => {
      if (!enableUploadSuccessTrack) return;
      trackRoboneoAssetUploadSuccess(items, insertTarget, trackLocation);
    },
    [enableUploadSuccessTrack, insertTarget, trackLocation],
  );
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const mediaLibraryAudioRef = useRef<HTMLAudioElement | null>(null);
  const [mediaLibraryAudioPlayingId, setMediaLibraryAudioPlayingId] = useState<string | null>(null);
  const [mediaLibraryAudioProgress, setMediaLibraryAudioProgress] = useState(0);
  /** 与资产页一致：筛选变化时递增，丢弃过期的「加载更多」结果 */
  const assetListFetchEpochRef = useRef(0);
  const lastLoadedAssetPageRef = useRef(0);
  const accumulatedAssetRowsRef = useRef<AssetItem[]>([]);
  const accumulatedMediaLibraryRowsRef = useRef<MediaLibraryItem[]>([]);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreAssetListRef = useRef<() => Promise<void>>(async () => {});
  const [loadingMore, setLoadingMore] = useState(false);
  const [listHasMore, setListHasMore] = useState(false);
  /** 切换个人/团队作用域时清空旧目录与选择态，随后由请求 effect 加载当前 OID 对应的数据。 */
  useEffect(() => {
    assetListFetchEpochRef.current += 1;
    accumulatedAssetRowsRef.current = [];
    accumulatedMediaLibraryRowsRef.current = [];
    lastLoadedAssetPageRef.current = 0;
    loadMoreInFlightRef.current = false;
    setFolders([]);
    setSelectedFolderId(undefined);
    setGroups([]);
    setLoading(false);
    setLoadingMore(false);
    setListHasMore(false);
    setSelectedIds(new Set());
    setSelectedCreatedByUid(undefined);
    setMemberDropdownOpen(false);
    setBatchMode(defaultBatchMode);
    setMediaLibraryManageBatchMode(false);
    setItemActionMenu(null);
    setFolderActionMenu(null);
    setMediaLibraryUploadMenuOpen(false);
    setPersonalAssetImportOpen(false);
    setCreateRoleModalOpen(false);
    setCreateRoleEditContext(null);
  }, [defaultBatchMode, teamOid]);
  /** 团队作用域变化时重新拉取成员，忽略切换团队或组件卸载后的过期响应。 */
  useEffect(() => {
    setTeamMembers([]);
    setTeamMembersLoadFailed(false);

    if (!isTeamAssetLibrary) {
      setTeamMembersLoading(false);
      return undefined;
    }

    let isActive = true;
    setTeamMembersLoading(true);

    /** 拉取当前团队成员，并仅写入当前仍有效的团队作用域。 */
    async function loadCurrentTeamMembers(): Promise<void> {
      try {
        const data = await getTeamMembers();
        if (isActive) {
          setTeamMembers(Array.isArray(data.list) ? data.list : []);
        }
      } catch {
        if (isActive) {
          setTeamMembers([]);
          setTeamMembersLoadFailed(true);
        }
      } finally {
        if (isActive) {
          setTeamMembersLoading(false);
        }
      }
    }

    loadCurrentTeamMembers().catch(() => {});

    return () => {
      isActive = false;
    };
  }, [isTeamAssetLibrary, teamOid]);
  useEffect(() => {
    if (allowedMaterialTypeValues.length === 0) {
      return;
    }
    if (!allowedMaterialTypeValues.includes(materialType)) {
      setMaterialType(allowedMaterialTypeValues[0]);
    }
  }, [allowedMaterialTypeValues, materialType]);
  /** 资产库列表的公共查询参数，团队态选中成员时通过创建人 UID 交由后端筛选。 */
  const mediaLibraryItemListBaseParams = useMemo((): Omit<MediaLibraryItemListParams, 'page' | 'page_size'> => {
    const params: Omit<MediaLibraryItemListParams, 'page' | 'page_size'> = {
      folder_id: selectedFolderId ?? 0,
    };
    if (isTeamAssetLibrary && selectedCreatedByUid != null) {
      params.created_by_uid = selectedCreatedByUid;
    }
    return params;
  }, [isTeamAssetLibrary, selectedCreatedByUid, selectedFolderId]);
  /**
   * 拉取第一页并重建分组（筛选/排序/视图模式变化时由 effect 调用）。
   *
   * @param signal 可选；卸载时 abort，避免卸载后 setState
   */
  const loadAssetList = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      assetListFetchEpochRef.current += 1;
      const fetchEpoch = assetListFetchEpochRef.current;
      accumulatedAssetRowsRef.current = [];
      accumulatedMediaLibraryRowsRef.current = [];
      lastLoadedAssetPageRef.current = 0;
      setListHasMore(false);
      // 素材库根目录（folder_id=0）右侧内容由文件夹树驱动，不请求 item/list：
      // 避免打开/新建文件夹的过渡态发起无谓的 folder_id=0 请求并导致右侧内容闪烁。
      if ((selectedFolderId ?? 0) === 0) {
        setGroups([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getMediaLibraryItemList({
          ...mediaLibraryItemListBaseParams,
          page: 1,
          page_size: DEFAULT_PAGE_SIZE,
        });
        if (useOfficialStore.getState().clientOId !== teamOid) return;
        if (signal?.aborted) return;
        if (fetchEpoch !== assetListFetchEpochRef.current) return;
        accumulatedMediaLibraryRowsRef.current = res.list;
        lastLoadedAssetPageRef.current = res.list.length > 0 ? 1 : 0;
        const { rawRows, items } = buildMediaLibraryModalRows(
          res.list,
          materialType,
          allowedMaterialTypeValues,
          sortOrder,
        );
        setGroups(buildAssetModalGroupsFromList(listGroupMode, rawRows, items, sortOrder));
        const total = Number.isFinite(res.total) ? res.total : res.list.length;
        setListHasMore(res.list.length > 0 && res.list.length < total);
        return;
      } catch (err) {
        if (signal?.aborted) return;
        if (fetchEpoch !== assetListFetchEpochRef.current) return;
        if (!isAssetLibraryApiMessageShown(err)) {
          message.error(languageStore.smartcatTranslationText((v) => v.get_assets_list_failed));
        }
      } finally {
        if (!signal?.aborted && fetchEpoch === assetListFetchEpochRef.current) {
          setLoading(false);
        }
      }
    },
    [
      selectedFolderId,
      mediaLibraryItemListBaseParams,
      materialType,
      allowedMaterialTypeValues,
      sortOrder,
      listGroupMode,
      languageStore,
      teamOid,
    ],
  );
  /**
   * 滚动接近底部时请求下一页，拼接到累加列表并重新分组（与 AssetManage `loadMoreAssetList` 一致）。
   */
  const loadMoreAssetList = useCallback(async (): Promise<void> => {
    if (!listHasMore || loading || loadingMore || loadMoreInFlightRef.current) {
      return;
    }
    const epochAtStart = assetListFetchEpochRef.current;
    const nextPage = lastLoadedAssetPageRef.current + 1;
    if (nextPage < 2) return;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const res = await getMediaLibraryItemList({
        ...mediaLibraryItemListBaseParams,
        page: nextPage,
        page_size: DEFAULT_PAGE_SIZE,
      });
      if (useOfficialStore.getState().clientOId !== teamOid) return;
      if (epochAtStart !== assetListFetchEpochRef.current) return;
      if (res.list.length === 0) {
        setListHasMore(false);
        return;
      }
      const combinedList = [...accumulatedMediaLibraryRowsRef.current, ...res.list];
      accumulatedMediaLibraryRowsRef.current = combinedList;
      lastLoadedAssetPageRef.current = nextPage;
      const { rawRows, items } = buildMediaLibraryModalRows(
        combinedList,
        materialType,
        allowedMaterialTypeValues,
        sortOrder,
      );
      setGroups(buildAssetModalGroupsFromList(listGroupMode, rawRows, items, sortOrder));
      const total = Number.isFinite(res.total) ? res.total : combinedList.length;
      setListHasMore(combinedList.length < total);
      return;
    } catch (err) {
      if (epochAtStart !== assetListFetchEpochRef.current) return;
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.load_more_fail));
      }
    } finally {
      loadMoreInFlightRef.current = false;
      if (epochAtStart === assetListFetchEpochRef.current) {
        setLoadingMore(false);
      }
    }
  }, [
    listHasMore,
    loading,
    loadingMore,
    mediaLibraryItemListBaseParams,
    materialType,
    allowedMaterialTypeValues,
    sortOrder,
    listGroupMode,
    languageStore,
    teamOid,
  ]);
  useLayoutEffect(() => {
    loadMoreAssetListRef.current = loadMoreAssetList;
  }, [loadMoreAssetList]);
  useEffect(() => {
    const ac = new AbortController();
    loadAssetList(ac.signal).catch(() => {});
    return () => ac.abort();
  }, [loadAssetList]);
  /**
   * 列表区域滚动：距底部 400px 内触发加载下一页（与 AssetManage `handleScroll` 加载逻辑一致）。
   */
  const handleAssetListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const loadMoreThresholdPx = 400;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < loadMoreThresholdPx) {
      loadMoreAssetListRef.current().catch(() => {});
    }
  }, []);
  /** 已拉取过计数的子文件夹 id -> total，防止 setFolders 触发的 effect 重复请求。 */
  const childFolderCountRef = useRef<Map<string, number>>(new Map());
  /** 拉取资产库文件夹树，并更新侧栏树形列表。 */
  const reloadMediaLibraryFolders = useCallback(async () => {
    const res = await getMediaLibraryFolderTree();
    if (useOfficialStore.getState().clientOId !== teamOid) return [];
    const nextFolders = flattenMediaLibraryFolderTree(
      res.list,
      unnamedFolderText,
      generatedCharacterText,
    );
    // 树刷新说明素材可能有增删/移动，子文件夹计数需要重新拉取。
    childFolderCountRef.current.clear();
    setFolders(nextFolders);
    return nextFolders;
  }, [generatedCharacterText, teamOid, unnamedFolderText]);
  useEffect(() => {
    reloadMediaLibraryFolders().catch((err) => {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.assets_get_list_fail));
      }
    });
  }, [languageStore, reloadMediaLibraryFolders]);
  /** 个人资产导入完成后同步刷新团队目录计数、当前素材列表与容量信息。 */
  const handlePersonalAssetImportComplete = useCallback(async (): Promise<void> => {
    await Promise.allSettled([
      reloadMediaLibraryFolders(),
      loadAssetList(),
      loadSubscribeInfo(),
    ]);
  }, [loadAssetList, loadSubscribeInfo, reloadMediaLibraryFolders]);
  const foldersOrdered = useMemo(() => sortFoldersForDropdown(folders), [folders]);
  const selectedFolder = useMemo(
    () => folders.find((folder) => Number(folder.id) === selectedFolderId),
    [folders, selectedFolderId],
  );
  /** 角色 IP 分类是额外入口，folder/tree 返回的历史生成角色目录仍需保留在侧栏。 */
  const rootMediaLibraryFolders = useMemo(
    () => foldersOrdered.filter((folder) => getFolderParentNumericId(folder) === 0),
    [foldersOrdered],
  );
  /** 右侧内容区展示当前文件夹的直接子文件夹。 */
  const currentChildFolders = useMemo(
    () => (selectedFolderId != null
      ? foldersOrdered.filter((folder) => getFolderParentNumericId(folder) === selectedFolderId)
      : []),
    [foldersOrdered, selectedFolderId],
  );
  /** folder/tree 接口不下发 asset_count，子文件夹卡片的资产数用 item/list 的 total 兜底拉取。 */
  useEffect(() => {
    if (currentChildFolders.length === 0) return undefined;
    const countMap = childFolderCountRef.current;
    const targets = currentChildFolders.filter((folder) => !countMap.has(folder.id));
    if (targets.length === 0) return undefined;
    let cancelled = false;
    targets.forEach((folder) => countMap.set(folder.id, -1));
    Promise.all(
      targets.map(async (folder) => {
        const folderId = getFolderNumericId(folder);
        if (!folderId) return null;
        try {
          const res = await getMediaLibraryItemList({ folder_id: folderId, page: 1, page_size: 1 });
          return { id: folder.id, total: Number(res.total) || 0 };
        } catch {
          countMap.delete(folder.id);
          return null;
        }
      }),
    ).then((results) => {
      const counts = results.filter(
        (
          result,
        ): result is {
          id: string;
          total: number;
        } => result != null,
      );
      if (cancelled || counts.length === 0) return;
      counts.forEach((count) => countMap.set(count.id, count.total));
      setFolders((prev) => prev.map((folder) => {
        const hit = counts.find((count) => count.id === folder.id);
        return hit && folder.assetCount !== hit.total ? { ...folder, assetCount: hit.total } : folder;
      }));
    });
    return () => {
      cancelled = true;
      // 清掉未完成的 in-flight 标记，避免中途取消后计数永远不再拉取。
      targets.forEach((folder) => {
        if (countMap.get(folder.id) === -1) {
          countMap.delete(folder.id);
        }
      });
    };
  }, [currentChildFolders]);
  /** 文件夹移动弹窗中的不可选目标原因，素材移动模式下保持为空。 */
  const moveFolderDisabledReasons = useMemo(
    () => buildFolderMoveDisabledReasons(foldersOrdered, moveFolderTarget, {
      cannotMoveToSelf: languageStore.smartcatTranslationText(
        (v) => v.cannot_move_to_self_foldercannot_move_to_self_folder,
      ),
      cannotMoveToDescendant: languageStore.smartcatTranslationText((v) => v.cannot_move_to_sub),
      alreadyInTargetDirectory: languageStore.smartcatTranslationText(
        (v) => v.already_in_this_directory,
      ),
      duplicateName: languageStore.smartcatTranslationText((v) => v.duplicate_folder_name_in_target_dir),
      depthLimitExceeded: languageStore.smartcatTranslationText((v) => v.folder_depth_limit_exceeded_cannot_move),
    }),
    [foldersOrdered, languageStore, moveFolderTarget],
  );
  /** 文件夹更多菜单里的同级调序状态，按当前父级展示顺序判断能否上移/下移。 */
  const folderActionMoveState = useMemo(
    () => getFolderSiblingMoveState(foldersOrdered, folderActionMenu?.folder),
    [folderActionMenu, foldersOrdered],
  );
  const folderActionMoveDisabledReason = useMemo(() => {
    if (!folderActionMenu?.folder) return undefined;
    return getFolderSubtreeMaxDepth(foldersOrdered, folderActionMenu.folder) >= 10
      ? languageStore.smartcatTranslationText((v) => v.folder_depth_already_at_limit_cannot_move)
      : undefined;
  }, [folderActionMenu, foldersOrdered, languageStore]);
  /** 当前文件夹面包屑路径，用于 Figma 的「角色 / 文件夹1」标题。 */
  const selectedFolderPath = useMemo(() => {
    if (!selectedFolder) return [];
    const byId = new Map(folders.map((folder) => [Number(folder.id), folder]));
    const path: FolderItem[] = [];
    let current: FolderItem | undefined = selectedFolder;
    const guard = new Set<number>();
    while (current) {
      const currentId = Number(current.id);
      if (!Number.isFinite(currentId) || guard.has(currentId)) break;
      guard.add(currentId);
      path.unshift(current);
      const parentId = getFolderParentNumericId(current);
      current = parentId > 0 ? byId.get(parentId) : undefined;
    }
    return path;
  }, [folders, selectedFolder]);
  /** 当前路径所属的一级文件夹，用于侧栏选中态跟随子文件夹。 */
  const selectedRootFolderId = useMemo(() => {
    const root = selectedFolderPath[0];
    return root ? Number(root.id) : selectedFolderId;
  }, [selectedFolderId, selectedFolderPath]);
  const mediaLibraryFolderDepthLimitText = languageStore.smartcatTranslationText((v) => v.folder_max_depth_10);
  const selectedFolderDepth = Number(selectedFolder?.depth) || 0;
  const canCreateMediaLibraryChildFolder = selectedFolderDepth < 10;
  const showCreateRoleEntry = isGenRoleMediaLibraryFolder(selectedFolder);
  /** 素材库首次进入默认选中第一个根文件夹，匹配 Figma 不展示“全部”的状态。 */
  useEffect(() => {
    if (folders.length === 0) return;
    if (selectedFolderId != null && folders.some((folder) => Number(folder.id) === selectedFolderId)) return;
    const firstRootFolder = rootMediaLibraryFolders[0] ?? folders[0];
    const firstRootFolderId = getFolderNumericId(firstRootFolder);
    setSelectedFolderId(firstRootFolderId);
  }, [folders, rootMediaLibraryFolders, selectedFolderId]);
  /** 打开新建文件夹弹窗，并允许菜单指定目标父文件夹。 */
  const openCreateMediaLibraryFolderDialogAtParent = useCallback(
    (parentFolder?: FolderItem | null) => {
      const parentDepth = Number(parentFolder?.depth) || 0;
      if (parentDepth >= 10) {
        message.warning(mediaLibraryFolderDepthLimitText);
        return;
      }
      setFolderCreateParentId(getFolderNumericId(parentFolder) ?? 0);
      setFolderNameTarget(null);
      setFolderNameDraft('');
      setFolderNameDialogOpen(true);
    },
    [mediaLibraryFolderDepthLimitText],
  );
  /** 侧栏「新建文件夹」固定创建根目录文件夹，避免误建到当前选中文件夹下。 */
  const openCreateMediaLibraryFolderDialog = useCallback(() => {
    openCreateMediaLibraryFolderDialogAtParent(null);
  }, [openCreateMediaLibraryFolderDialogAtParent]);
  /** 右侧内容区「新建文件夹」沿用当前位置，在当前文件夹下创建子文件夹。 */
  const openCreateMediaLibraryChildFolderDialog = useCallback(() => {
    openCreateMediaLibraryFolderDialogAtParent(selectedFolder);
  }, [openCreateMediaLibraryFolderDialogAtParent, selectedFolder]);
  /** 打开重命名文件夹弹窗；是否允许系统默认夹由资产库接口最终判断。 */
  const openRenameMediaLibraryFolderDialog = useCallback((folder: FolderItem) => {
    setFolderNameTarget(folder);
    setFolderCreateParentId(Number(folder.parentId ?? 0));
    setFolderNameDraft(folder.name);
    setFolderNameDialogOpen(true);
  }, []);
  /** 提交新建或重命名文件夹，并刷新素材库文件夹树。 */
  const handleSubmitMediaLibraryFolderName = useCallback(async () => {
    const name = folderNameDraft.trim();
    if (!name) {
      message.warning(languageStore.smartcatTranslationText((v) => v.assets_created_folder_error));
      return;
    }
    if (Array.from(name).length > MEDIA_LIBRARY_MAX_NAME_LENGTH) {
      message.warning(folderNameTooLongText);
      return;
    }
    let createdFolderIdToSelect: number | undefined;
    let optimisticCreatedFolder: FolderItem | undefined;
    try {
      if (folderNameTarget) {
        await renameMediaLibraryFolder(Number(folderNameTarget.id), name);
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
      } else {
        const res = await createMediaLibraryFolder({ parent_id: folderCreateParentId, name });
        const createdFolderId = Number(res.folder_id);
        const createdFolderSortOrder = getFolderMinimumSortOrderAfterGenRoleFolders(folders, folderCreateParentId);
        optimisticCreatedFolder = Number.isFinite(createdFolderId) && createdFolderId > 0
          ? buildOptimisticMediaLibraryFolder(
            folders,
            folderCreateParentId,
            createdFolderId,
            name,
            createdFolderSortOrder,
          )
          : undefined;
        if (optimisticCreatedFolder) {
          createdFolderIdToSelect = createdFolderId;
          const folderToInsert = optimisticCreatedFolder;
          setFolders((prev) => insertOptimisticMediaLibraryFolder(prev, folderToInsert));
          setSelectedFolderId(createdFolderId);
          try {
            const moveResult = await moveMediaLibraryFolder({
              folder_id: createdFolderId,
              target_parent_id: folderCreateParentId,
              sort_order: createdFolderSortOrder,
            });
            if (moveResult?.async && moveResult.task_id) {
              await waitForMediaLibraryFolderMoveTask(moveResult.task_id);
            }
          } catch (moveErr) {
            console.warn('[AssetLibraryModalContent] move created media library folder to top failed', moveErr);
          }
        }
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
      }
      setFolderNameDialogOpen(false);
      setFolderNameTarget(null);
      setFolderNameDraft('');
      const nextFolders = await reloadMediaLibraryFolders();
      if (createdFolderIdToSelect && optimisticCreatedFolder) {
        const reloadedCreatedFolder = nextFolders.find(
          (folder) => getFolderNumericId(folder) === createdFolderIdToSelect,
        );
        const folderToInsert = reloadedCreatedFolder ?? optimisticCreatedFolder;
        setFolders((prev) => insertOptimisticMediaLibraryFolder(prev, folderToInsert));
        setSelectedFolderId(createdFolderIdToSelect);
      }
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
    }
  }, [
    folderNameTooLongText,
    folderNameDraft,
    folderCreateParentId,
    folderNameTarget,
    folders,
    languageStore,
    reloadMediaLibraryFolders,
  ]);
  /** 打开删除文件夹确认；是否允许系统默认夹由资产库接口最终判断。 */
  const openDeleteMediaLibraryFolderConfirm = useCallback((folder: FolderItem) => {
    setDeleteConfirmState({ kind: 'folder', folder });
  }, []);
  /** 读取当前资产库文件夹 id；上传入口必须落到当前选中的具体文件夹。 */
  const getSelectedMediaLibraryFolderId = useCallback((): number | undefined => {
    if (selectedFolderId == null) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_a_folder));
      return undefined;
    }
    return selectedFolderId;
  }, [languageStore, selectedFolderId]);
  /** 批量保存资产库上传项；资产库视图同步刷新目录树，历史记录视图只刷新自身列表。 */
  const persistMediaLibraryUploadItems = useCallback(
    async (
      paramsList: Array<MediaLibraryItemSaveParams | null>,
      options?: {
        showSuccessMessage?: boolean;
      },
    ): Promise<boolean> => {
      const showSuccessMessage = options?.showSuccessMessage !== false;
      const validParams = paramsList.filter((params): params is MediaLibraryItemSaveParams => params != null);
      if (!validParams.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.images_videos_and_audio_files_can_be_uploaded_));
        return false;
      }
      if (!validateAssetStorageBeforeWrite(sumMediaLibraryItemSaveFileSize(validParams))) {
        return false;
      }
      const results = await Promise.allSettled(validParams.map((params) => saveMediaLibraryItem(params)));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedResults = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (successCount > 0) {
        await Promise.all([
          reloadMediaLibraryFolders(),
          loadAssetList(),
          loadSubscribeInfo().catch(() => {}),
        ]);
        if (showSuccessMessage) {
          message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
        }
      }
      if (failedResults.length > 0 && successCount > 0) {
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
      if (
        failedResults.length > 0
        && successCount === 0
        && !failedResults.some((result) => isAssetLibraryApiMessageShown(result.reason))
      ) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
      return successCount > 0;
    },
    [languageStore, loadAssetList, loadSubscribeInfo, reloadMediaLibraryFolders, validateAssetStorageBeforeWrite],
  );
  /** 创建角色结果图复用画布的“存为资产”弹框与保存流程。 */
  const handleOpenCreateRoleSaveDialog = useCallback(async (
    image: ThreeViewImage,
    formSnapshot: RoleGenFormValues,
    roleTaskId: string,
  ): Promise<void> => {
    if (createRoleSaveDialogOpenRef.current) return;
    createRoleSaveDialogOpenRef.current = true;
    const trackRoomId = resolveAssetModalTrackRoomId(insertTarget);
    try {
      await showMediaLibrarySaveModal({
        initialName: normalizeMediaLibrarySaveName(formSnapshot.name, `role-${Date.now()}`),
        initialFolderSystemKey: MEDIA_LIBRARY_GEN_ROLE_SYSTEM_KEY,
        previewInfo: {
          icon: 'pictureBold',
          url: image.url,
        },
        zIndex: CREATE_ROLE_SAVE_DIALOG_Z_INDEX,
        onSubmit: async ({ folderId, name }) => {
          let params: MediaLibraryItemSaveParams | null = null;
          try {
            trackRoboneoAssetRoleSaveUpload({
              roomId: trackRoomId,
              taskId: roleTaskId,
              mediaUrl: image.url,
            });
            params = await buildMediaLibrarySaveParamsFromRoleGenImage(
              image,
              formSnapshot,
              roleTaskId,
              folderId,
              name,
            );
            if (!params) {
              throw new Error(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
            }
            const savedItem = await saveMediaLibraryItem(params);
            trackRoboneoAssetRoleSaveResult({
              success: true,
              roomId: trackRoomId,
              taskId: roleTaskId,
              mediaUrl: params.url,
              assetId: String(savedItem.item_id || ''),
            });
            await Promise.allSettled([
              reloadMediaLibraryFolders(),
              loadAssetList(),
              loadSubscribeInfo(),
            ]);
          } catch (error) {
            trackRoboneoAssetRoleSaveResult({
              success: false,
              roomId: trackRoomId,
              taskId: roleTaskId,
              mediaUrl: params?.url || image.url,
            });
            throw error;
          }
        },
      });
    } finally {
      createRoleSaveDialogOpenRef.current = false;
    }
  }, [
    languageStore,
    loadAssetList,
    loadSubscribeInfo,
    reloadMediaLibraryFolders,
    insertTarget,
  ]);
  /** 点击资产库弹窗「本地上传」，先校验当前文件夹，再触发隐藏文件选择器。 */
  const handleMediaLibraryLocalUploadClick = useCallback(() => {
    if (mediaLibraryUploading) return;
    const folderId = getSelectedMediaLibraryFolderId();
    if (!folderId) return;
    setMediaLibraryUploadMenuOpen(false);
    mediaLibraryUploadInputRef.current?.click();
  }, [getSelectedMediaLibraryFolderId, mediaLibraryUploading]);
  /** 本地选择文件后复用 TeamFlow 上传 SDK，完成安审后保存到当前资产库文件夹。 */
  const handleMediaLibraryLocalUploadFiles = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const folderId = getSelectedMediaLibraryFolderId();
      const files = Array.from(e.currentTarget.files ?? []);
      e.currentTarget.value = '';
      if (!folderId || files.length === 0 || mediaLibraryUploading) return;
      const selectedFileBytes = files.reduce((total, file) => total + file.size, 0);
      if (!validateAssetStorageBeforeWrite(selectedFileBytes)) return;
      setMediaLibraryUploading(true);
      dealUploadFile(
        {
          files,
          controlImagesOptions: {
            accept: IMAGE_TYPES,
          },
          controlVideosOptions: {
            accept: VIDEO_TYPES,
          },
          controlAudiosOptions: {
            accept: AUDIO_TYPES,
          },
          controlFilesOptions: {
            accept: [],
            importCount: 0,
          },
          isCheckSafe: true,
          from: 'workflow',
          location: 'workflow_teams_add',
        },
        {
          callback: (uploadedFiles) => {
            // 上传层已按大小或格式展示具体错误，空结果不再进入资产保存层触发泛化类型提示。
            if (uploadedFiles.length === 0) {
              setMediaLibraryUploading(false);
              return;
            }
            (async () => {
              try {
                await persistMediaLibraryUploadItems(
                  uploadedFiles.map((file) => buildMediaLibrarySaveParamsFromUploadedFile(file, folderId)),
                );
              } finally {
                setMediaLibraryUploading(false);
              }
            })();
          },
        },
      ).catch(() => {
        setMediaLibraryUploading(false);
        message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_upload_fail_text));
      });
    },
    [
      getSelectedMediaLibraryFolderId,
      languageStore,
      mediaLibraryUploading,
      persistMediaLibraryUploadItems,
      validateAssetStorageBeforeWrite,
    ],
  );
  /** 点击资产库弹窗「历史记录上传」，打开历史记录资产选择器并限制为图/音/视频。 */
  const handleMediaLibraryHistoryUploadClick = useCallback(() => {
    if (mediaLibraryUploading) return;
    const folderId = getSelectedMediaLibraryFolderId();
    if (!folderId) return;
    setMediaLibraryUploadMenuOpen(false);
    setHistoryUploadPickerOpen(true);
  }, [getSelectedMediaLibraryFolderId, mediaLibraryUploading]);
  /** 历史记录上传复用本地上传数量上限，超出的选择项直接跳过并提示。 */
  const limitMediaLibraryHistoryUploadItems = useCallback(
    (items: AssetItemView[]): AssetItemView[] => {
      const counts: Record<MediaLibraryUploadMaterialType, number> = {
        image: 0,
        video: 0,
        audio: 0,
      };
      let exceeded = false;
      const accepted: AssetItemView[] = [];
      items.forEach((item) => {
        const uploadMaterialType = resolveAssetViewMediaLibraryMaterialType(item);
        if (!uploadMaterialType) return;
        if (counts[uploadMaterialType] >= MEDIA_LIBRARY_HISTORY_UPLOAD_LIMITS[uploadMaterialType]) {
          exceeded = true;
          return;
        }
        counts[uploadMaterialType] += 1;
        accepted.push(item);
      });
      if (exceeded) {
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_file_limit));
      }
      return accepted;
    },
    [languageStore],
  );
  /** 将历史记录选择的素材批量保存到当前资产库文件夹。 */
  const handleMediaLibraryHistoryUploadItems = useCallback(
    (items: AssetItemView[]) => {
      if (mediaLibraryUploading) return;
      const folderId = getSelectedMediaLibraryFolderId();
      if (!folderId || items.length === 0) return;
      const limitedItems = limitMediaLibraryHistoryUploadItems(items);
      if (!limitedItems.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.images_videos_and_audio_files_can_be_uploaded_));
        return;
      }
      setMediaLibraryUploading(true);
      (async () => {
        try {
          const paramsList = await Promise.all(
            limitedItems.map((item) => buildMediaLibrarySaveParamsFromHistoryItem(item, folderId)),
          );
          const saved = await persistMediaLibraryUploadItems(paramsList, { showSuccessMessage: false });
          if (saved) {
            setHistoryUploadPickerOpen(false);
            window.setTimeout(() => {
              message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
            }, 0);
          }
        } finally {
          setMediaLibraryUploading(false);
        }
      })();
    },
    [
      getSelectedMediaLibraryFolderId,
      languageStore,
      limitMediaLibraryHistoryUploadItems,
      mediaLibraryUploading,
      persistMediaLibraryUploadItems,
    ],
  );
  const visibleItemsOrdered = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  /** 资产库弹窗曝光：组件挂载一次上报一次，并保留弹窗级兼容事件。 */
  useEffect(() => {
    trackRoboneoAssetLibraryShow(insertTarget);
    // 仅在弹窗挂载时上报一次，room_id 由 insertTarget 解析。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** 已上报曝光的卡片 id，避免列表刷新 / 滚动加载时重复上报。 */
  const reportedCardShowIdsRef = useRef<Set<string>>(new Set());
  /** 素材库卡片曝光：可见列表新增卡片时按 id 去重上报。 */
  useEffect(() => {
    visibleItemsOrdered.forEach((item) => {
      if (reportedCardShowIdsRef.current.has(item.id)) return;
      reportedCardShowIdsRef.current.add(item.id);
      trackRoboneoAssetLibraryCardShow(item, insertTarget);
    });
  }, [visibleItemsOrdered, insertTarget]);
  /** 批量管理时读取当前可见且已选中的资产库 item_id。 */
  const selectedMediaLibraryItemIds = useMemo(() => {
    return visibleItemsOrdered
      .filter((item) => selectedIds.has(item.id))
      .map(getMediaLibraryItemNumericId)
      .filter((id): id is number => id != null);
  }, [selectedIds, visibleItemsOrdered]);
  /** 批量「全选」作用域：内置插入时仅为当前目标支持的类型，否则为当前列表全部可见项 */
  const batchSelectScopeItems = useMemo(() => {
    if (mediaLibraryManageBatchMode || !limitBuiltInBatchMedia) return visibleItemsOrdered;
    return visibleItemsOrdered.filter((i) => isBuiltInModalBatchSelectableMaterialType(insertTarget, i.materialType));
  }, [insertTarget, limitBuiltInBatchMedia, mediaLibraryManageBatchMode, visibleItemsOrdered]);
  const batchSelectScopeCount = batchSelectScopeItems.length;
  const visibleIdSet = useMemo(() => new Set(visibleItemsOrdered.map((i) => i.id)), [visibleItemsOrdered]);
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visibleIdSet.has(id)) next.add(id);
        else changed = true;
      });
      if (!changed && next.size === prev.size) return prev;
      return next;
    });
  }, [visibleIdSet]);
  /** 退出任一批量模式并清空选择，避免管理态影响外部托管的批量插入。 */
  const exitBatchMode = useCallback(() => {
    setSelectedIds(new Set());
    setBatchMode(false);
    setMediaLibraryManageBatchMode(false);
  }, []);
  /**
   * 多选底栏「取消选择」：清空已选，仍停留在批量模式（对齐 Figma 33766:106933）。
   */
  const clearBatchSelectionOnly = useCallback(() => {
    setSelectedIds(new Set());
  }, []);
  /** 顶部「批量处理」按来源进入资产管理或主体选择模式；再次点击统一退出批量态。 */
  const toggleMediaLibraryManageBatchMode = useCallback(() => {
    if (batchMode) {
      exitBatchMode();
    } else {
      setSelectedIds(new Set());
      setMediaLibraryManageBatchMode(!isSubjectLibraryPicker);
      setBatchMode(true);
    }
    setItemActionMenu(null);
    setFolderActionMenu(null);
    setMediaLibraryUploadMenuOpen(false);
  }, [batchMode, exitBatchMode, isSubjectLibraryPicker]);
  /** 在主体选择上限内切换单条资产选中态。 */
  const toggleSelectAssetId = useCallback((id: string) => {
    setSelectedIds((prev) => toggleBatchAssetSelection(
      prev,
      id,
      subjectPickerMaxSelectableCount,
    ));
  }, [subjectPickerMaxSelectableCount]);
  /**
   * 批量模式下切换单条选中，并通知外部（卡片点击、勾选框与键盘操作共用）。
   *
   * @param item 列表项
   */
  const handleBatchItemToggle = useCallback(
    (item: AssetItemView) => {
      if (
        !mediaLibraryManageBatchMode
        && limitBuiltInBatchMedia
        && !isBuiltInModalBatchSelectableMaterialType(insertTarget, item.materialType)
      ) {
        return;
      }
      if (!canToggleBatchAssetSelection(selectedIds, item.id, subjectPickerMaxSelectableCount)) {
        return;
      }
      const wasSelected = selectedIds.has(item.id);
      const selected = !wasSelected;
      toggleSelectAssetId(item.id);
      if (!mediaLibraryManageBatchMode) {
        onAssetBatchItemClick?.({ item, selected });
      }
    },
    [
      insertTarget,
      limitBuiltInBatchMedia,
      mediaLibraryManageBatchMode,
      onAssetBatchItemClick,
      selectedIds,
      subjectPickerMaxSelectableCount,
      toggleSelectAssetId,
    ],
  );
  /** 全选时同样截取到外部选择上限，避免隐藏入口或键盘操作绕过约束。 */
  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      if (batchSelectScopeCount === 0) return prev;
      const allOn = batchSelectScopeItems.every((i) => prev.has(i.id));
      if (allOn) return new Set();
      const nextIds = batchSelectScopeItems.map((i) => i.id);
      return new Set(subjectPickerMaxSelectableCount == null
        ? nextIds
        : nextIds.slice(0, subjectPickerMaxSelectableCount));
    });
  }, [batchSelectScopeCount, batchSelectScopeItems, subjectPickerMaxSelectableCount]);
  /** 打开单条素材重命名弹窗。 */
  const openRenameMediaLibraryItemDialog = useCallback((item: AssetItemView) => {
    setItemNameTarget(item);
    setItemNameDraft(assetDisplayTitle(item.raw, item.mediaUrl, item.id).slice(0, MEDIA_LIBRARY_MAX_NAME_LENGTH));
    setItemNameDialogOpen(true);
    setItemActionMenu(null);
  }, []);
  /** 提交单条素材重命名，并刷新当前列表。 */
  const handleSubmitMediaLibraryItemName = useCallback(async () => {
    const itemId = getMediaLibraryItemNumericId(itemNameTarget);
    const name = itemNameDraft.trim();
    if (!itemId || !name) {
      message.warning(languageStore.smartcatTranslationText((v) => v.asset_name));
      return;
    }
    if (Array.from(name).length > MEDIA_LIBRARY_MAX_NAME_LENGTH) {
      message.warning(assetNameTooLongText);
      return;
    }
    try {
      await renameMediaLibraryItem(itemId, name);
      setItemNameDialogOpen(false);
      setItemNameTarget(null);
      setItemNameDraft('');
      await loadAssetList();
      message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
    }
  }, [assetNameTooLongText, itemNameDraft, itemNameTarget, languageStore, loadAssetList]);
  /** 打开单条素材删除确认。 */
  const openDeleteMediaLibraryItemConfirm = useCallback((item: AssetItemView) => {
    setDeleteConfirmState({ kind: 'item', item });
    setItemActionMenu(null);
  }, []);
  /** 打开单条或批量素材移动弹窗；默认不选中文件夹，未选择时确认按钮置灰。 */
  const openMoveMediaLibraryItemsDialog = useCallback(
    (itemIds: number[]) => {
      if (!itemIds.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
        return;
      }
      setMoveItemIds(itemIds);
      setMoveFolderTarget(null);
      setMoveTargetFolderId(undefined);
      setMoveDialogOpen(true);
      setItemActionMenu(null);
    },
    [languageStore],
  );
  /** 单条素材移动入口：从视图项解析 item_id 后复用批量移动弹窗。 */
  const openMoveSingleMediaLibraryItemDialog = useCallback(
    (item: AssetItemView) => {
      const itemId = getMediaLibraryItemNumericId(item);
      if (!itemId) {
        message.error(languageStore.smartcatTranslationText((v) => v.asset_id_is_invalid));
        return;
      }
      openMoveMediaLibraryItemsDialog([itemId]);
    },
    [languageStore, openMoveMediaLibraryItemsDialog],
  );
  /** 批量删除入口：确认前先校验当前选择能映射到资产库 item_id。 */
  const openBatchDeleteMediaLibraryItemsConfirm = useCallback(() => {
    if (!selectedMediaLibraryItemIds.length) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    setDeleteConfirmState({ kind: 'batch', itemIds: selectedMediaLibraryItemIds });
  }, [languageStore, selectedMediaLibraryItemIds]);
  /** 批量移动入口：确认前先校验当前选择能映射到资产库 item_id。 */
  const openBatchMoveMediaLibraryItemsDialog = useCallback(() => {
    if (!selectedMediaLibraryItemIds.length) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    openMoveMediaLibraryItemsDialog(selectedMediaLibraryItemIds);
  }, [languageStore, openMoveMediaLibraryItemsDialog, selectedMediaLibraryItemIds]);
  /** 执行文件夹移动接口，兼容同步成功和大子树异步任务两种响应。 */
  const executeMediaLibraryFolderMove = useCallback(
    async (params: MediaLibraryFolderMoveParams): Promise<void> => {
      const result = await moveMediaLibraryFolder(params);
      if (result?.async && !result.task_id) {
        throw new MediaLibraryFolderMoveTaskError(
          languageStore.smartcatTranslationText((v) => v.folder_move_task_missing_task_id),
        );
      }
      if (result?.async && result.task_id) {
        await waitForMediaLibraryFolderMoveTask(result.task_id);
      }
      await reloadMediaLibraryFolders();
      await loadAssetList();
    },
    [languageStore, loadAssetList, reloadMediaLibraryFolders],
  );
  /** 按资产库语义执行文件夹、单条素材或批量素材删除。 */
  const handleConfirmAssetDelete = useCallback(async () => {
    if (!deleteConfirmState) return;
    try {
      if (deleteConfirmState.kind === 'folder') {
        const folderId = Number(deleteConfirmState.folder.id);
        await deleteMediaLibraryFolder(folderId);
        const nextFolders = await reloadMediaLibraryFolders();
        if (selectedFolderId != null && !nextFolders.some((folder) => Number(folder.id) === selectedFolderId)) {
          setSelectedFolderId(undefined);
        } else {
          await loadAssetList();
        }
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_success));
      } else if (deleteConfirmState.kind === 'item') {
        const itemId = getMediaLibraryItemNumericId(deleteConfirmState.item);
        if (!itemId) throw new Error('invalid media library item id');
        await deleteMediaLibraryItem(itemId);
        await Promise.all([
          reloadMediaLibraryFolders(),
          loadAssetList(),
          loadSubscribeInfo().catch(() => {}),
        ]);
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_success));
      } else if (deleteConfirmState.kind === 'batch') {
        await batchActionMediaLibraryItem({
          action_type: 'delete',
          item_ids: deleteConfirmState.itemIds,
        });
        exitBatchMode();
        await Promise.all([
          reloadMediaLibraryFolders(),
          loadAssetList(),
          loadSubscribeInfo().catch(() => {}),
        ]);
        message.success(languageStore.smartcatTranslationText((v) => v.deleted_assets));
      }
      setDeleteConfirmState(null);
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_fail));
      }
    }
  }, [
    deleteConfirmState,
    exitBatchMode,
    languageStore,
    loadAssetList,
    loadSubscribeInfo,
    reloadMediaLibraryFolders,
    selectedFolderId,
  ]);
  /** 提交移动素材或文件夹到目标文件夹，并刷新当前列表。 */
  const handleSubmitMediaLibraryMove = useCallback(async () => {
    if (moveTargetFolderId == null) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_a_folder));
      return;
    }
    try {
      if (moveFolderTarget) {
        const movingFolderId = getFolderNumericId(moveFolderTarget);
        if (!movingFolderId) throw new Error('invalid media library folder id');
        const targetFolderExists = moveTargetFolderId === 0
          || foldersOrdered.some((folder) => getFolderNumericId(folder) === moveTargetFolderId);
        const movingFolderExists = foldersOrdered.some((folder) => getFolderNumericId(folder) === movingFolderId);
        if (!targetFolderExists || !movingFolderExists) {
          message.error(languageStore.smartcatTranslationText((v) => v.assets_invalid_folder_id));
          return;
        }
        const disabledReason = moveFolderDisabledReasons.get(moveTargetFolderId);
        if (disabledReason) {
          message.warning(disabledReason);
          return;
        }
        await executeMediaLibraryFolderMove({
          folder_id: movingFolderId,
          target_parent_id: moveTargetFolderId,
          sort_order: getFolderMoveDefaultSortOrder(foldersOrdered, moveTargetFolderId, movingFolderId),
        });
        setMoveDialogOpen(false);
        setMoveFolderTarget(null);
        setMoveTargetFolderId(undefined);
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
        return;
      }
      if (!moveItemIds.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
        return;
      }
      await batchActionMediaLibraryItem({
        action_type: 'move',
        item_ids: moveItemIds,
        target_folder: moveTargetFolderId,
      });
      setMoveDialogOpen(false);
      setMoveFolderTarget(null);
      setMoveItemIds([]);
      setMoveTargetFolderId(undefined);
      exitBatchMode();
      // 素材移动会改变源/目标文件夹的资产数量，需同步刷新文件夹树。
      await Promise.all([reloadMediaLibraryFolders(), loadAssetList()]);
      message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(
          err instanceof MediaLibraryFolderMoveTaskError
            ? err.message
            : languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again),
        );
      }
    }
  }, [
    executeMediaLibraryFolderMove,
    exitBatchMode,
    foldersOrdered,
    languageStore,
    loadAssetList,
    moveFolderDisabledReasons,
    moveFolderTarget,
    moveItemIds,
    moveTargetFolderId,
    reloadMediaLibraryFolders,
  ]);
  /** 执行文件夹同级上移/下移；不引入拖拽，直接把目标 1-based 位置传给 folder/move。 */
  const handleMoveMediaLibraryFolderWithinParent = useCallback(
    async (folder: FolderItem, direction: 'up' | 'down') => {
      const folderId = getFolderNumericId(folder);
      const nextSortOrder = getFolderSiblingMoveSortOrder(foldersOrdered, folder, direction);
      if (!folderId || nextSortOrder == null) return;
      try {
        setFolderActionMenu(null);
        await executeMediaLibraryFolderMove({
          folder_id: folderId,
          target_parent_id: getFolderParentNumericId(folder),
          sort_order: nextSortOrder,
        });
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
      } catch (err) {
        if (!isAssetLibraryApiMessageShown(err)) {
          message.error(
            err instanceof MediaLibraryFolderMoveTaskError
              ? err.message
              : languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again),
          );
        }
      }
    },
    [executeMediaLibraryFolderMove, foldersOrdered, languageStore],
  );
  useEffect(() => {
    /** 点击所有弹层外部时统一关闭筛选、更多菜单和上传菜单。 */
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (memberDropdownRef.current?.contains(target)) return;
      if (itemActionMenuRef.current?.contains(target)) return;
      if (folderActionMenuRef.current?.contains(target)) return;
      if (mediaLibraryUploadMenuRef.current?.contains(target)) return;
      setMemberDropdownOpen(false);
      setItemActionMenu(null);
      setFolderActionMenu(null);
      setMediaLibraryUploadMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const selectedFolderName = useMemo(() => {
    const allLabel = languageStore.smartcatTranslationText((v) => v.roboneo_web_All);
    if (selectedFolderId == null) return allLabel;
    return folders.find((f) => f.id === String(selectedFolderId))?.name ?? allLabel;
  }, [selectedFolderId, folders, languageStore]);
  /** 资产库从非管理批量态切到管理批量态时上报一次编辑栏曝光。 */
  useEffect(() => {
    if (
      mediaLibraryManageBatchMode
      && !previousMediaLibraryManageBatchModeForTrackRef.current
    ) {
      trackRoboneoAssetBatchBarShow(selectedFolderName);
    }
    previousMediaLibraryManageBatchModeForTrackRef.current = mediaLibraryManageBatchMode;
  }, [mediaLibraryManageBatchMode, selectedFolderName]);
  /** 为成员筛选项补充“我”标记，并保持按钮和菜单使用同一套成员名称。 */
  const getTeamMemberLabel = useCallback((member: TeamMemberListItem): string => {
    const displayName = getTeamMemberDisplayName(member);
    if (currentUserUid && String(member.uid) === String(currentUserUid)) {
      return `${displayName}${currentUserSuffixText}`;
    }
    return displayName;
  }, [currentUserSuffixText, currentUserUid]);
  /** 根据筛选 UID 定位成员实体，用于在按钮上展示名称。 */
  const selectedTeamMember = useMemo(() => {
    return teamMembers.find((member) => String(member.uid) === selectedCreatedByUid);
  }, [selectedCreatedByUid, teamMembers]);
  const teamMemberFilterLabel = selectedTeamMember
    ? getTeamMemberLabel(selectedTeamMember)
    : filterByMemberText;
  /** 打开成员筛选时关闭其它资产库浮层，避免同级菜单互相叠压。 */
  const toggleTeamMemberDropdown = useCallback(() => {
    setItemActionMenu(null);
    setFolderActionMenu(null);
    setMediaLibraryUploadMenuOpen(false);
    setMemberDropdownOpen((open) => !open);
  }, []);
  /** 点击已选成员会清除筛选；点击其他成员则按其创建人 UID 重新拉取资产库列表。 */
  const handleTeamMemberSelect = useCallback((member: TeamMemberListItem) => {
    const memberUid = String(member.uid);
    setSelectedCreatedByUid((selectedUid) => (selectedUid === memberUid ? undefined : memberUid));
    setMemberDropdownOpen(false);
  }, []);
  /** 素材库面包屑返回上级文件夹；一级文件夹不展示返回按钮。 */
  const handleMediaLibraryBackToParent = useCallback(() => {
    const parentId = getFolderParentNumericId(selectedFolder);
    if (parentId > 0) {
      setSelectedFolderId(parentId);
    }
  }, [selectedFolder]);
  /** 打开素材卡片更多菜单，并关闭其它悬浮菜单。 */
  const openItemActionMenu = useCallback((item: AssetItemView, anchorEl: HTMLElement) => {
    const rect = anchorEl.getBoundingClientRect();
    setFolderActionMenu(null);
    setMediaLibraryUploadMenuOpen(false);
    setItemActionMenu({
      item,
      left: Math.max(8, rect.right - 124),
      top: rect.bottom + 6,
    });
  }, []);
  /** 从角色素材卡片进入重新编辑，并将保存时记录的参数和任务 id 交给创建角色弹框。 */
  const handleReEditRoleAsset = useCallback((context: RoleGenEditContext): void => {
    setItemActionMenu(null);
    setCreateRoleEditContext(context);
    setCreateRoleModalOpen(true);
  }, []);
  /** 打开文件夹更多菜单，并关闭其它悬浮菜单。 */
  const openFolderActionMenu = useCallback((folder: FolderItem, anchorEl: HTMLElement) => {
    const rect = anchorEl.getBoundingClientRect();
    setItemActionMenu(null);
    setMediaLibraryUploadMenuOpen(false);
    setFolderActionMenu({
      folder,
      left: rect.right + 6,
      top: Math.max(8, rect.top),
    });
  }, []);
  /** 菜单为 fixed 定位，渲染后按实际尺寸钳制进视窗，避免底部文件夹/素材的菜单被裁剪且无法滚动查看。 */
  useLayoutEffect(() => {
    if (!folderActionMenu) return;
    const rect = folderActionMenuRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextLeft = Math.max(8, Math.min(folderActionMenu.left, window.innerWidth - rect.width - 8));
    const nextTop = Math.max(8, Math.min(folderActionMenu.top, window.innerHeight - rect.height - 8));
    if (nextLeft !== folderActionMenu.left || nextTop !== folderActionMenu.top) {
      setFolderActionMenu({ ...folderActionMenu, left: nextLeft, top: nextTop });
    }
  }, [folderActionMenu]);
  useLayoutEffect(() => {
    if (!itemActionMenu) return;
    const rect = itemActionMenuRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextLeft = Math.max(8, Math.min(itemActionMenu.left, window.innerWidth - rect.width - 8));
    const nextTop = Math.max(8, Math.min(itemActionMenu.top, window.innerHeight - rect.height - 8));
    if (nextLeft !== itemActionMenu.left || nextTop !== itemActionMenu.top) {
      setItemActionMenu({ ...itemActionMenu, left: nextLeft, top: nextTop });
    }
  }, [itemActionMenu]);
  /** 打开文件夹移动弹窗；目标默认不选中，符合 Figma 未选中时确认置灰的状态。 */
  const openMoveMediaLibraryFolderDialog = useCallback(
    (folder: FolderItem) => {
      const disabledReason = getFolderSubtreeMaxDepth(foldersOrdered, folder) >= 10
        ? languageStore.smartcatTranslationText((v) => v.folder_depth_already_at_limit_cannot_move)
        : undefined;
      if (disabledReason) {
        message.warning(disabledReason);
        setFolderActionMenu(null);
        return;
      }
      setFolderActionMenu(null);
      setMoveItemIds([]);
      setMoveFolderTarget(folder);
      setMoveTargetFolderId(undefined);
      setMoveDialogOpen(true);
    },
    [foldersOrdered, languageStore],
  );
  /** 根据资产库打开来源生成单条素材操作文案，避免主体库选择场景误导为添加到画布。 */
  const insertHint = useMemo(
    () => assetActionLabel
      ?? languageStore.smartcatTranslationText((v) => {
        if (pickerSource === 'subjectLibrary') return v.add_to_main_body;
        return insertTarget === 'workflow' ? v.asset_send_to_workflow : v.add_to_canvas;
      }),
    [assetActionLabel, languageStore, insertTarget, pickerSource],
  );
  const batchPrimaryLabel = useMemo(
    () => batchConfirmLabel
      ?? languageStore.smartcatTranslationText((v) => (insertTarget === 'workflow' ? v.asset_send_to_workflow : v.add_to_canvas)),
    [batchConfirmLabel, languageStore, insertTarget],
  );
  /** 首页与 Agent Team 输入框资产选择器上报新增弹框添加埋点。 */
  const trackInputAssetPopupAdd = useCallback((items: AssetItemView[]) => {
    if (pickerSource !== 'subjectLibrary') return;
    if (trackLocation !== 'agent_teams_page' && trackLocation !== 'home_page') return;
    const location = trackLocation === 'agent_teams_page' ? 'agent_team_page' : trackLocation;
    trackRoboneoAssetPopAddClick(
      items,
      resolveAssetModalTrackRoomId(insertTarget),
      selectedFolderName,
      location,
    );
  }, [insertTarget, pickerSource, selectedFolderName, trackLocation]);
  /** 资产库卡片被激活时上报个人资产卡片点击事件。 */
  const trackAssetLibraryCardClick = useCallback((item: AssetItemView) => {
    trackRoboneoAssetLibraryCardClick(item, insertTarget);
  }, [insertTarget]);
  /** 将单条资产添加到外部托管场景、工作流或画布，并在成功后按场景上报埋点。 */
  const handleAddToCanvas = useCallback(
    async (item: AssetItemView) => {
      const { mediaUrl, materialType: itemMaterialType } = item;
      if (!mediaUrl?.trim()) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
        return;
      }
      trackInputAssetPopupAdd([item]);
      if (enableAddCanvasClickTrack) {
        trackRoboneoAssetAddCanvasClick(item, insertTarget);
      }
      if (onAssetItemClick) {
        onAssetItemClick(item);
        trackAssetUploadSuccess([item]);
        return;
      }
      if (insertTarget === 'workflow') {
        if (!onInsertWorkflowMedia) {
          message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
          return;
        }
        if (!isWorkflowInsertableMaterialType(itemMaterialType)) {
          message.warning(languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_on_in_workflow));
          return;
        }
        const wItem = assetItemToWorkflowMediaItem(item);
        if (!wItem) {
          message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
          return;
        }
        try {
          onInsertWorkflowMedia([wItem]);
          trackAssetUploadSuccess([item]);
          if (closeOnBatchConfirm) {
            setOpenAssetPopover?.(false);
          }
        } catch {
          message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
        }
        return;
      }
      if (!isCanvasInsertableMaterialType(itemMaterialType)) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_in_canvas));
        return;
      }
      try {
        await insertAssetItemToCanvas(item);
        trackAssetUploadSuccess([item]);
        if (closeOnBatchConfirm) {
          setOpenAssetPopover?.(false);
        }
      } catch {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
    },
    [
      enableAddCanvasClickTrack,
      insertTarget,
      languageStore,
      onInsertWorkflowMedia,
      onAssetItemClick,
      closeOnBatchConfirm,
      setOpenAssetPopover,
      trackInputAssetPopupAdd,
      trackAssetUploadSuccess,
    ],
  );

  /** 将角色预览图交给资产库既有个人画布或团队画布插入流程。 */
  const handleAddCreateRoleImageToCanvas = useCallback(async (
    image: ThreeViewImage,
    formSnapshot: RoleGenFormValues,
  ): Promise<void> => {
    if (createRoleAddToCanvasDisabledReason) {
      message.warning(createRoleAddToCanvasDisabledReason);
      return;
    }
    const item = buildRoleGenCanvasItem(image, formSnapshot);
    if (!item) {
      message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
      return;
    }
    await handleAddToCanvas(item);
  }, [createRoleAddToCanvasDisabledReason, handleAddToCanvas, languageStore]);

  /**
   * 预览弹框内是否展示添加按钮，与卡片 hover 添加能力保持一致。
   */
  const shouldShowPreviewAddButton = useCallback(
    (item: AssetItemView): boolean => {
      return Boolean(onAssetItemClick) || shouldShowAssetInsertHoverBadge(insertTarget, item.materialType);
    },
    [insertTarget, onAssetItemClick],
  );
  /**
   * 构造公共预览头部/底部的添加按钮插槽。
   */
  const buildAssetPreviewAddSlot = useCallback(
    (item: AssetItemView, previewItems?: AssetItemView[]): React.ReactElement | null => {
      if (!shouldShowPreviewAddButton(item)) return null;
      return (
        <AssetPreviewAddToCanvasSlot
          item={item}
          previewItems={previewItems}
          label={insertHint}
          onAddToCanvas={handleAddToCanvas}
        />
      );
    },
    [handleAddToCanvas, insertHint, shouldShowPreviewAddButton],
  );
  /** 批量添加资产到外部托管场景、工作流或画布，并按实际成功列表上报埋点。 */
  const handleBatchAdd = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    const ordered = visibleItemsOrdered.filter((i) => selectedIds.has(i.id));
    trackInputAssetPopupAdd(ordered);
    if (enableAddCanvasClickTrack) {
      ordered.forEach((item) => trackRoboneoAssetAddCanvasClick(item, insertTarget));
    }
    if (onAssetBatchConfirm) {
      onAssetBatchConfirm(ordered);
      trackAssetUploadSuccess(ordered);
      if (closeOnBatchConfirm) {
        setOpenAssetPopover?.(false);
      }
      exitBatchMode();
      return;
    }
    if (insertTarget === 'workflow') {
      if (!onInsertWorkflowMedia) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
        return;
      }
      const workflowAllowed = ordered.filter((i) => isWorkflowInsertableMaterialType(i.materialType));
      const skippedWorkflowUnsupported = ordered.length - workflowAllowed.length;
      if (skippedWorkflowUnsupported > 0) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_selected_cannot_be_opened_in_workflow));
      }
      const wItems = workflowAllowed
        .map(assetItemToWorkflowMediaItem)
        .filter((x): x is WorkflowCanvasMediaItem => x != null);
      if (!wItems.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
        return;
      }
      try {
        onInsertWorkflowMedia(wItems);
        trackAssetUploadSuccess(workflowAllowed);
        if (closeOnBatchConfirm) {
          setOpenAssetPopover?.(false);
        }
        exitBatchMode();
      } catch {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
      return;
    }
    const canvasItems = ordered.filter((i) => isCanvasInsertableMaterialType(i.materialType));
    const skippedCanvasUnsupported = ordered.length - canvasItems.length;
    if (skippedCanvasUnsupported > 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.assets_selected_cannot_be_opened_in_canvas));
    }
    const withUrl = canvasItems.filter((i) => i.mediaUrl?.trim());
    if (!withUrl.length) {
      message.warning(languageStore.smartcatTranslationText((v) => v.assets_selected_cannot_be_opened_in_canvas));
      return;
    }
    let ok = 0;
    let fail = 0;
    const successItems: AssetItemView[] = [];
    /* 顺序插入避免画布节点在同一落点堆叠；需等待上一张完成再插下一张 */
    for (const item of withUrl) {
      try {
        // eslint-disable-next-line no-await-in-loop -- 批量插入画布必须串行
        await insertAssetItemToCanvas(item);
        ok += 1;
        successItems.push(item);
      } catch {
        fail += 1;
      }
    }
    if (ok > 0) {
      trackAssetUploadSuccess(successItems);
      if (closeOnBatchConfirm) {
        setOpenAssetPopover?.(false);
      }
      exitBatchMode();
    }
    if (fail > 0 && ok === 0) {
      message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    } else if (fail > 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    }
  }, [
    selectedIds,
    visibleItemsOrdered,
    closeOnBatchConfirm,
    enableAddCanvasClickTrack,
    insertTarget,
    onInsertWorkflowMedia,
    onAssetBatchConfirm,
    setOpenAssetPopover,
    exitBatchMode,
    languageStore,
    trackInputAssetPopupAdd,
    trackAssetUploadSuccess,
  ]);
  const assetPreviewSaveLocation: AssetPreviewSaveLocation = isTeamAssetLibrary
    ? 'team_asset'
    : 'asset_preview_page';
  /** 停止资产库音频预览，并复位卡片播放态。 */
  const stopMediaLibraryAudioPreview = useCallback(() => {
    const audio = mediaLibraryAudioRef.current;
    if (audio) {
      audio.pause();
    }
    setMediaLibraryAudioPlayingId(null);
    setMediaLibraryAudioProgress(0);
  }, []);
  /** 复用全局 MediaPreview 打开图片预览，发送到画布入口的卡片点击不再承担插入职责。 */
  const handleAssetImagePreview = useCallback(
    (item: AssetItemView) => {
      const { previewItems, sourceItems, currentIndex } = buildAssetMediaPreviewList(
        visibleItemsOrdered,
        item,
        assetPreviewSaveLocation,
      );
      if (previewItems.length === 0) return;
      stopMediaLibraryAudioPreview();
      openPreview({
        previewImageList: previewItems,
        initialIndex: currentIndex,
        previewShowZoom: false,
        previewShowDownload: canDownload,
        previewHeaderMenuSlots: buildAssetPreviewAddSlot(item, sourceItems),
      });
    },
    [
      assetPreviewSaveLocation,
      buildAssetPreviewAddSlot,
      canDownload,
      stopMediaLibraryAudioPreview,
      visibleItemsOrdered,
    ],
  );
  /** 复用全局 MediaPreview 打开文本/脚本预览。 */
  const handleAssetDocumentTextPreview = useCallback(
    (item: AssetItemView) => {
      const previewItem = buildAssetDocumentTextPreviewItem(item, assetPreviewSaveLocation);
      if (!previewItem) return;
      stopMediaLibraryAudioPreview();
      openPreview({
        previewImageList: [previewItem],
        initialIndex: 0,
        previewShowZoom: false,
        previewShowDownload: canDownload,
        previewHeaderMenuSlots: buildAssetPreviewAddSlot(item, [item]),
      });
    },
    [assetPreviewSaveLocation, buildAssetPreviewAddSlot, canDownload, stopMediaLibraryAudioPreview],
  );
  /** 根据素材类型打开对应预览。 */
  const handleAssetCardPreview = useCallback(
    (item: AssetItemView) => {
      if (isAssetImagePreviewable(item) || isAssetVideoPreviewable(item)) {
        handleAssetImagePreview(item);
        return;
      }
      handleAssetDocumentTextPreview(item);
    },
    [handleAssetDocumentTextPreview, handleAssetImagePreview],
  );
  /** 鼠标点击 hover 操作按钮后清理焦点，避免鼠标移出卡片后因 focus-within 仍显示操作层。 */
  const blurHoverActionButtonAfterMouseClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 0) {
      e.currentTarget.blur();
    }
  }, []);
  /** 打开视频素材的全局 MediaPreview，并要求弹框内视频立即播放。 */
  const openMediaLibraryVideoPreviewItem = useCallback(
    (item: AssetItemView) => {
      const { previewItems, sourceItems, currentIndex } = buildAssetMediaPreviewList(
        visibleItemsOrdered,
        item,
        assetPreviewSaveLocation,
      );
      if (previewItems.length === 0) {
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_no_preview));
        return;
      }
      stopMediaLibraryAudioPreview();
      openPreview({
        previewImageList: previewItems,
        initialIndex: currentIndex,
        previewShowZoom: false,
        previewShowDownload: canDownload,
        previewHeaderMenuSlots: buildAssetPreviewAddSlot(item, sourceItems),
      });
    },
    [
      assetPreviewSaveLocation,
      buildAssetPreviewAddSlot,
      canDownload,
      languageStore,
      stopMediaLibraryAudioPreview,
      visibleItemsOrdered,
    ],
  );
  /** 点击视频预览按钮时复用全局 MediaPreview 弹框，并阻止卡片点击逻辑冒泡。 */
  const handleMediaLibraryVideoPreviewOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, item: AssetItemView) => {
      e.preventDefault();
      e.stopPropagation();
      openMediaLibraryVideoPreviewItem(item);
    },
    [openMediaLibraryVideoPreviewItem],
  );
  /** 播放或暂停当前音频卡片；菜单预览也复用该逻辑，音频不打开全局弹框。 */
  const playOrStopMediaLibraryAudioItem = useCallback(
    async (item: AssetItemView) => {
      const audio = mediaLibraryAudioRef.current;
      const mediaUrl = item.mediaUrl?.trim();
      if (!audio || !mediaUrl) {
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_no_preview));
        return;
      }
      if (mediaLibraryAudioPlayingId === item.id && !audio.paused) {
        stopMediaLibraryAudioPreview();
        return;
      }
      audio.pause();
      if (audio.src !== mediaUrl) {
        audio.src = mediaUrl;
        audio.load();
      }
      audio.currentTime = 0;
      setMediaLibraryAudioProgress(0);
      setMediaLibraryAudioPlayingId(item.id);
      try {
        await audio.play();
      } catch {
        setMediaLibraryAudioPlayingId(null);
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
    },
    [languageStore, mediaLibraryAudioPlayingId, stopMediaLibraryAudioPreview],
  );
  /** 点击音频预览按钮时播放或暂停当前卡片，避免 hover 自动触发音频。 */
  const handleMediaLibraryAudioPreviewToggle = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>, item: AssetItemView) => {
      e.preventDefault();
      e.stopPropagation();
      await playOrStopMediaLibraryAudioItem(item);
    },
    [playOrStopMediaLibraryAudioItem],
  );
  /** 资产卡片菜单中的预览入口，按类型复用图片/文本/脚本/视频预览，音频仅在卡片内播放。 */
  const handleMediaLibraryItemMenuPreview = useCallback(
    (item: AssetItemView) => {
      setItemActionMenu(null);
      if (item.materialType === 'audio') {
        playOrStopMediaLibraryAudioItem(item);
        return;
      }
      if (isAssetVideoPreviewable(item)) {
        openMediaLibraryVideoPreviewItem(item);
        return;
      }
      if (isAssetCardClickPreviewable(item)) {
        handleAssetCardPreview(item);
        return;
      }
      message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_no_preview));
    },
    [handleAssetCardPreview, languageStore, openMediaLibraryVideoPreviewItem, playOrStopMediaLibraryAudioItem],
  );
  /** 资产卡片菜单中的下载入口；历史记录按国内水印偏好选择地址。 */
  const handleAssetItemDownload = useCallback(
    async (item: AssetItemView) => {
      setItemActionMenu(null);
      const originalUrl = (item.mediaUrl || item.coverUrl || '').trim();
      if (!originalUrl) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
        return;
      }
      const downloadUrl = originalUrl;
      if (isTeamAssetLibrary) {
        trackTeamAssetHistorySave('canvas_save_click', [item], 'team_asset');
      }
      const hideLoading = message.loading('RoboNeo_Web_loading1', 0);
      try {
        await downloadFileFromUrl(downloadUrl, getAssetModalDownloadFilename(item), {
          preferSuggestedFilename: true,
        });
        if (isTeamAssetLibrary) {
          trackTeamAssetHistorySave('canvas_save_success', [item], 'team_asset');
        }
      } catch {
        if (isTeamAssetLibrary) {
          trackTeamAssetHistorySave('canvas_save_fail', [item], 'team_asset');
        }
        message.error(languageStore.smartcatTranslationText((v) => v.assets_download_fail));
      } finally {
        hideLoading();
      }
    },
    [isTeamAssetLibrary, languageStore],
  );
  /** 同步隐藏 audio 元素的播放进度，用于高亮资产库音频波形。 */
  const handleMediaLibraryAudioTimeUpdate = useCallback(() => {
    const audio = mediaLibraryAudioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) {
      return;
    }
    setMediaLibraryAudioProgress(Math.min(1, Math.max(0, audio.currentTime / audio.duration)));
  }, []);
  /** 拖拽资产库音频卡片波形控制器时，同步隐藏 audio 的播放进度。 */
  const handleMediaLibraryAudioSeek = useCallback(
    (itemId: string, progress: number) => {
      const audio = mediaLibraryAudioRef.current;
      if (!audio || mediaLibraryAudioPlayingId !== itemId || !Number.isFinite(audio.duration) || audio.duration <= 0) {
        return;
      }
      const nextProgress = Math.min(1, Math.max(0, progress));
      audio.currentTime = audio.duration * nextProgress;
      setMediaLibraryAudioProgress(nextProgress);
    },
    [mediaLibraryAudioPlayingId],
  );
  /** 音频预览结束时清理播放态，避免卡片停留在暂停按钮状态。 */
  const handleMediaLibraryAudioEnded = useCallback(() => {
    setMediaLibraryAudioPlayingId(null);
    setMediaLibraryAudioProgress(0);
  }, []);
  /** 当前列表变化后，停止已经不可见的音频预览。 */
  useEffect(() => {
    if (!mediaLibraryAudioPlayingId) {
      return;
    }
    const stillVisible = visibleItemsOrdered.some((item) => item.id === mediaLibraryAudioPlayingId);
    if (!stillVisible) {
      stopMediaLibraryAudioPreview();
    }
  }, [mediaLibraryAudioPlayingId, stopMediaLibraryAudioPreview, visibleItemsOrdered]);
  /** 组件卸载时停止隐藏 audio，避免弹窗关闭后继续播放。 */
  useEffect(() => {
    const audio = mediaLibraryAudioRef.current;
    return () => {
      audio?.pause();
    };
  }, []);
  const allVisibleSelected = batchSelectScopeCount > 0
    && selectedIds.size === batchSelectScopeCount
    && batchSelectScopeItems.every((i) => selectedIds.has(i.id));
  const someVisibleSelected = batchSelectScopeCount > 0 && batchSelectScopeItems.some((i) => selectedIds.has(i.id)) && !allVisibleSelected;
  const deleteConfirmContent = useMemo(() => {
    if (!deleteConfirmState) {
      return {
        title: '',
        note: '',
        confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
      };
    }
    if (deleteConfirmState.kind === 'folder') {
      return {
        title: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_history_delete_hint_text),
        note: languageStore.smartcatTranslationText((v) => v.delete_confirmation_modal),
        confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
      };
    }
    if (deleteConfirmState.kind === 'batch') {
      const selectedCount = deleteConfirmState.itemIds.length;
      return {
        title: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_history_delete_hint_text),
        note: languageStore
          .smartcatTranslationText(
            (v) => v['Confirm_deletion_of_number_selected_asset(s)_from_the_asset_library?'],
          )
          .replace('{number}', String(selectedCount)),
        confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
      };
    }
    return {
      title: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_history_delete_hint_text),
      note: languageStore.smartcatTranslationText((v) => v.confirmcancellation),
      confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
    };
  }, [deleteConfirmState, languageStore]);
  const canMediaLibraryBackToParent = getFolderParentNumericId(selectedFolder) > 0;
  const selectedFolderPathLabel = selectedFolderPath.map((folder) => folder.name).join(' / ') || selectedFolderName;
  let mediaLibraryBreadcrumbFolders = selectedFolderPath;
  if (mediaLibraryBreadcrumbFolders.length === 0 && selectedFolder) {
    mediaLibraryBreadcrumbFolders = [selectedFolder];
  }
  const mediaLibraryGridEmpty = !showCreateRoleEntry && currentChildFolders.length === 0 && visibleItemsOrdered.length === 0;
  const itemActionRoleEditContext = itemActionMenu
    ? buildRoleGenEditContext(itemActionMenu.item)
    : null;
  return (
    <AssetLibraryPanelShell
      activeTab={panelNavigation.activeTab}
      tabs={panelNavigation.tabs}
      title={panelNavigation.title}
      closeLabel={panelNavigation.closeLabel}
      onTabChange={panelNavigation.onTabChange}
      setOpenAssetPopover={setOpenAssetPopover}
    >
      <audio
        ref={mediaLibraryAudioRef}
        className={styles.mediaLibraryAudioElement}
        preload="none"
        crossOrigin="anonymous"
        onTimeUpdate={handleMediaLibraryAudioTimeUpdate}
        onEnded={handleMediaLibraryAudioEnded}
      >
        <track kind="captions" />
      </audio>
      <div className={cn(styles.panelBody, styles.panelBodyMedia)}>
        <aside
          className={cn(styles.mediaLibrarySidebar, { [styles.ipSidebar]: enableIpCategory && ipSelected && !ipCategoryContent })}
          aria-label={languageStore.smartcatTranslationText((v) => v.asset_folder)}
        >
          {enableIpCategory && ipSelected && !ipCategoryContent ? <span className={styles.ipSidebarTitle}>{languageStore.smartcatTranslationText((v) => v.asset_type)}</span> : (
            <button
              type="button"
              className={styles.mediaLibraryNewFolderBtn}
              onClick={openCreateMediaLibraryFolderDialog}
            >
              <IconFont name="plusBold" fontSize={14} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.new_folder)}</span>
            </button>
          )}
          <div className={styles.mediaLibraryFolderList}>
            {enableIpCategory && (
            <button type="button" className={cn(styles.mediaLibraryFolderItem, { [styles.mediaLibraryFolderItemSelected]: ipSelected })} aria-pressed={ipSelected} onClick={() => setIpSelected(true)}>
              {/* 角色 IP 使用与其他资产分类一致的文件夹，不把 IP 身份当作媒体文件夹 ID。 */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img className={styles.mediaLibrarySidebarFolderIcon} src={ASSET_LIBRARY_FOLDER_IMAGES[theme][ipSelected ? 'open' : 'closed']} alt="" crossOrigin="anonymous" />
              <span>{languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_ip_asset_title)}</span>
            </button>
            )}
            {rootMediaLibraryFolders.map((folder) => {
              const idNum = Number(folder.id);
              const selected = !ipSelected && selectedRootFolderId === idNum;
              const hideFolderActionMenu = isGenRoleMediaLibraryFolder(folder);
              return (
                <div
                  key={folder.id}
                  className={cn(styles.mediaLibraryFolderItem, {
                    [styles.mediaLibraryFolderItemWithAction]: !hideFolderActionMenu,
                    [styles.mediaLibraryFolderItemSelected]: selected,
                  })}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setIpSelected(false); setSelectedFolderId(idNum); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setIpSelected(false);
                      setSelectedFolderId(idNum);
                    }
                  }}
                  title={folder.name}
                >
                  {isTeamAssetLibrary ? (
                    <IconFont
                      className={styles.mediaLibrarySidebarFolderIcon}
                      name="teamAssetLibraryFolder"
                      fontSize={40}
                      color="#F0F5FF"
                      style={{ height: 32 }}
                      aria-hidden
                    />
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      crossOrigin="anonymous"
                      className={styles.mediaLibrarySidebarFolderIcon}
                      src={ASSET_LIBRARY_FOLDER_IMAGES[theme][selected ? 'open' : 'closed']}
                      alt=""
                    />
                  )}
                  <span className={styles.mediaLibrarySidebarFolderName}>{folder.name}</span>
                  {!hideFolderActionMenu && (
                    <button
                      type="button"
                      className={styles.mediaLibraryFolderMoreBtn}
                      aria-label={languageStore.smartcatTranslationText((v) => v.roboneo_web_more)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openFolderActionMenu(folder, e.currentTarget);
                      }}
                    >
                      <IconFont name="ellipsisV2Fill" fontSize={14} color="currentColor" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <AssetStorageCard onAction={handleAssetStorageAction} />
        </aside>
        {enableIpCategory && ipSelected ? (ipCategoryContent ?? (
          <MyIpAssetContent
            assetType="personal"
            requireCanvas={insertTarget === 'agentTeam'}
            trackRoomId={resolveAssetModalTrackRoomId(insertTarget)}
            onIpItemsAdd={onIpItemsAdd}
            maxIpSelectableCount={maxIpSelectableCount}
            addLabel={ipAddLabel ?? assetActionLabel}
            hideMaterials={hideIpMaterials}
            hideIpDetail={hideIpDetail}
          />
        )) : (
          <div className={styles.panelMain}>
            <div
              className={cn(styles.mediaLibraryContentHeader, {
                [styles.mediaLibraryContentHeaderTeam]: isTeamAssetLibrary,
              })}
            >
              <div className={styles.mediaLibraryContentTopRow}>
                <div className={styles.mediaLibraryBreadcrumb}>
                  {canMediaLibraryBackToParent && (
                  <button
                    type="button"
                    className={styles.mediaLibraryBackBtn}
                    aria-label={languageStore.smartcatTranslationText((v) => v.asset_folder)}
                    onClick={handleMediaLibraryBackToParent}
                  >
                    <IconFont name="chevronLeftBold" fontSize={14} color="currentColor" />
                  </button>
                  )}
                  <span
                    className={cn(styles.mediaLibraryBreadcrumbText, {
                      [styles.mediaLibraryRootTitle]: !canMediaLibraryBackToParent,
                    })}
                    title={selectedFolderPathLabel}
                  >
                    {mediaLibraryBreadcrumbFolders.map((folder, index, path) => {
                      const isCurrentFolder = index === path.length - 1;
                      return (
                        <React.Fragment key={folder.id}>
                          <span
                            className={cn({
                              [styles.mediaLibraryBreadcrumbParent]: !isCurrentFolder,
                              [styles.mediaLibraryBreadcrumbCurrent]: isCurrentFolder,
                            })}
                          >
                            {folder.name}
                          </span>
                          {!isCurrentFolder && <span className={styles.mediaLibraryBreadcrumbSeparator}>/</span>}
                        </React.Fragment>
                      );
                    })}
                  </span>
                </div>
                {isTeamAssetLibrary && (
                <div className={styles.dropdownWrap} ref={memberDropdownRef}>
                  <button
                    type="button"
                    className={cn(styles.mediaLibraryMemberFilterBtn, {
                      [styles.sortBtnActive]: memberDropdownOpen,
                    })}
                    onClick={toggleTeamMemberDropdown}
                    aria-haspopup="listbox"
                    aria-expanded={memberDropdownOpen}
                  >
                    <span className={styles.teamMemberFilterLabel} title={teamMemberFilterLabel}>
                      {teamMemberFilterLabel}
                    </span>
                    <IconFont name="chevronDownFill" fontSize={8} color="currentColor" aria-hidden />
                  </button>
                  {memberDropdownOpen && (
                    <div
                      className={cn(styles.dropMenu, styles.teamMemberDropMenu)}
                      role="listbox"
                      aria-label={teamMembersText}
                    >
                      <div className={styles.teamMemberMenuTitle}>
                        {teamMembersText}
                      </div>
                      {teamMembersLoading && (
                        <div className={styles.teamMemberMenuState} role="status">
                          {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading)}
                        </div>
                      )}
                      {!teamMembersLoading && teamMembersLoadFailed && (
                        <div className={styles.teamMemberMenuState} role="status">
                          {languageStore.smartcatTranslationText((v) => v.roboneo_web_loading_failed)}
                        </div>
                      )}
                      {!teamMembersLoading && !teamMembersLoadFailed && teamMembers.length === 0 && (
                        <div className={styles.teamMemberMenuState} role="status">
                          {noTeamMembersText}
                        </div>
                      )}
                      {!teamMembersLoading && !teamMembersLoadFailed && teamMembers.map((member) => {
                        const memberUid = String(member.uid);
                        const selected = selectedCreatedByUid === memberUid;
                        const memberLabel = getTeamMemberLabel(member);
                        return (
                          <button
                            key={memberUid}
                            type="button"
                            className={cn(styles.teamMemberMenuItem, {
                              [styles.teamMemberMenuItemSelected]: selected,
                            })}
                            onClick={() => handleTeamMemberSelect(member)}
                            role="option"
                            aria-selected={selected}
                            title={memberLabel}
                          >
                            <span className={styles.teamMemberMenuItemMain}>
                              <TeamMemberAvatar member={member} />
                              <span className={styles.teamMemberName}>{memberLabel}</span>
                            </span>
                            {selected && (
                              <IconFont
                                name="checkV2Fill"
                                fontSize={10}
                                color="var(--content-primary)"
                                className={styles.teamMemberMenuCheck}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}
              </div>
              <div className={styles.mediaLibraryContentActions}>
                <input
                  ref={mediaLibraryUploadInputRef}
                  type="file"
                  multiple
                  accept={MEDIA_LIBRARY_UPLOAD_ACCEPT}
                  className={styles.mediaLibraryUploadInput}
                  onChange={handleMediaLibraryLocalUploadFiles}
                />
                <button
                  type="button"
                  className={styles.mediaLibraryFeatureEntryBtn}
                  aria-pressed={batchMode}
                  onClick={toggleMediaLibraryManageBatchMode}
                >
                  <IconFont
                    name={batchMode ? 'arrowRightBrackets' : 'multipleChoiceBold'}
                    fontSize={14}
                    color="currentColor"
                    aria-hidden
                  />
                  <span>
                    {batchMode
                      ? languageStore.smartcatTranslationText((v) => v.asset_quit_batch)
                      : batchProcessingText}
                  </span>
                </button>
                {isTeamAssetLibrary && (
                <button
                  type="button"
                  className={styles.mediaLibraryFeatureEntryBtn}
                  onClick={() => {
                    setItemActionMenu(null);
                    setFolderActionMenu(null);
                    setMediaLibraryUploadMenuOpen(false);
                    setMemberDropdownOpen(false);
                    setPersonalAssetImportOpen(true);
                  }}
                >
                  <IconFont
                    name="arrowTurnRightBlack"
                    fontSize={14}
                    color="currentColor"
                    aria-hidden
                  />
                  <span>{importFromPersonalAssetsText}</span>
                </button>
                )}
                <button
                  type="button"
                  className={cn(styles.mediaLibraryContentNewFolderBtn, {
                    [styles.mediaLibraryContentNewFolderBtnNested]: canMediaLibraryBackToParent,
                  })}
                  disabled={!canCreateMediaLibraryChildFolder}
                  title={!canCreateMediaLibraryChildFolder ? mediaLibraryFolderDepthLimitText : undefined}
                  onClick={openCreateMediaLibraryChildFolderDialog}
                >
                  <IconFont name="folderOpenAddBold" fontSize={14} color="currentColor" />
                  <span>{languageStore.smartcatTranslationText((v) => v.new_folder)}</span>
                </button>
                <div className={styles.mediaLibraryUploadMenuWrap} ref={mediaLibraryUploadMenuRef}>
                  <button
                    type="button"
                    className={styles.mediaLibraryContentUploadBtn}
                    disabled={mediaLibraryUploading}
                    aria-haspopup="menu"
                    aria-expanded={mediaLibraryUploadMenuOpen}
                    onClick={() => {
                      setItemActionMenu(null);
                      setFolderActionMenu(null);
                      setMediaLibraryUploadMenuOpen((v) => !v);
                    }}
                  >
                    <IconFont name="uploadBold" fontSize={14} color="currentColor" />
                    <span>
                      {languageStore.smartcatTranslationText((v) => (mediaLibraryUploading ? v.uploading_ : v.ai_enhance_upload))}
                    </span>
                    <IconFont name="chevronDownFill" fontSize={10} color="currentColor" />
                  </button>
                  {mediaLibraryUploadMenuOpen && (
                  <div className={styles.mediaLibraryUploadMenu} role="menu">
                    <button
                      type="button"
                      className={styles.mediaLibraryUploadMenuItem}
                      disabled={mediaLibraryUploading}
                      onClick={handleMediaLibraryLocalUploadClick}
                    >
                      <IconFont name="uploadBold" fontSize={16} color="currentColor" />
                      <span>{languageStore.smartcatTranslationText((v) => v.upload_from_local)}</span>
                    </button>
                    <button
                      type="button"
                      className={styles.mediaLibraryUploadMenuItem}
                      disabled={mediaLibraryUploading}
                      onClick={handleMediaLibraryHistoryUploadClick}
                    >
                      <IconFont name="clockBold" fontSize={16} color="currentColor" />
                      <span>{languageStore.smartcatTranslationText((v) => v.upload_from_history)}</span>
                    </button>
                  </div>
                  )}
                </div>
              </div>
            </div>

            <div
              ref={scrollContentRef}
              className={cn(styles.scrollContent, styles.mediaLibraryScrollContent)}
              onScroll={handleAssetListScroll}
            >
              {loading && (
              <div className={styles.loadingWrap}>
                {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading1)}
              </div>
              )}
              {!loading && mediaLibraryGridEmpty && (
              <div className={styles.mediaLibraryEmptyWrap} role="status">
                {languageStore.smartcatTranslationText((v) => v.empty)}
              </div>
              )}
              {!loading && !mediaLibraryGridEmpty && (
              <div className={styles.mediaLibraryGrid}>
                {showCreateRoleEntry && (
                  <CreateRoleEntryCard
                    disabled={mediaLibraryManageBatchMode}
                    onClick={() => {
                      setCreateRoleEditContext(null);
                      setCreateRoleModalOpen(true);
                    }}
                  />
                )}
                {currentChildFolders.map((folder) => (
                  <MediaLibraryFolderGridCard
                    key={`folder-${folder.id}`}
                    folder={folder}
                    disabled={mediaLibraryManageBatchMode}
                    hideActionMenu={isGenRoleMediaLibraryFolder(folder)}
                    onOpen={setSelectedFolderId}
                    onActionMenu={openFolderActionMenu}
                  />
                ))}
                {visibleItemsOrdered.map((item) => {
                  const isCardSelected = selectedIds.has(item.id);
                  const batchIneligible = batchMode
                    && !mediaLibraryManageBatchMode
                    && limitBuiltInBatchMedia
                    && !isBuiltInModalBatchSelectableMaterialType(insertTarget, item.materialType);
                  const batchSelectionLimitDisabled = batchMode
                    && !isCardSelected
                    && !canToggleBatchAssetSelection(
                      selectedIds,
                      item.id,
                      subjectPickerMaxSelectableCount,
                    );
                  const canClickPreviewAsset = isAssetCardClickPreviewable(item);
                  const showInsertHoverBadge = !batchMode
                    && (Boolean(onAssetItemClick) || shouldShowAssetInsertHoverBadge(insertTarget, item.materialType));
                  let cardAriaLabel = languageStore.smartcatTranslationText((v) => v.asset_library);
                  if (batchMode) {
                    if (batchIneligible) {
                      cardAriaLabel = insertTarget === 'workflow'
                        ? languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_on_in_workflow)
                        : languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_in_canvas);
                    } else {
                      cardAriaLabel = languageStore.smartcatTranslationText((v) => v.asset_unselect);
                    }
                  } else if (canClickPreviewAsset) {
                    cardAriaLabel = languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_card_preview);
                  } else if (shouldShowAssetInsertHoverBadge(insertTarget, item.materialType)) {
                    cardAriaLabel = insertHint;
                  }
                  const canvasDragEnabled = insertTarget === 'canvas'
                    && !batchMode
                    && !onAssetItemClick
                    && isCanvasInsertableMaterialType(item.materialType)
                    && !batchIneligible;
                  const title = getMediaLibraryAssetTitle(item);
                  const typeLabel = getMediaLibraryAssetTypeLabel(item, selectedFolderName, {
                    audio: languageStore.smartcatTranslationText((v) => v.audio),
                    video: languageStore.smartcatTranslationText((v) => v.roboneo_web_video),
                    script: languageStore.smartcatTranslationText((v) => v.script),
                    text: languageStore.smartcatTranslationText((v) => v.roboneo_web_text),
                    document: languageStore.smartcatTranslationText((v) => v.asset_type_files),
                    image: languageStore.smartcatTranslationText((v) => v.pictures),
                  });
                  const formatLabel = getMediaLibraryAssetFormatLabel(item);
                  const showPreviewPlayBadge = item.materialType === 'video' || item.materialType === 'audio';
                  const audioPlaying = mediaLibraryAudioPlayingId === item.id;
                  const previewPlaying = item.materialType === 'audio' ? audioPlaying : false;
                  const mediaLibraryCardClickable = batchMode || canClickPreviewAsset;
                  return (
                    <AssetModalItemRoot
                      key={item.id}
                      item={item}
                      canvasDragEnabled={canvasDragEnabled}
                      className={cn(styles.mediaLibraryAssetCard, {
                        [styles.mediaLibraryAssetCardBatch]: batchMode,
                        [styles.mediaLibraryAssetCardDisabled]: batchIneligible,
                        [styles.assetBatchSelectionLimitDisabled]: batchSelectionLimitDisabled,
                      })}
                      style={{}}
                      role={mediaLibraryCardClickable ? 'button' : undefined}
                      tabIndex={mediaLibraryCardClickable ? 0 : -1}
                      aria-label={cardAriaLabel}
                      aria-pressed={batchMode && !batchIneligible ? isCardSelected : undefined}
                      aria-disabled={batchIneligible || batchSelectionLimitDisabled || undefined}
                      onClick={() => {
                        trackAssetLibraryCardClick(item);
                        if (batchMode) handleBatchItemToggle(item);
                        else if (canClickPreviewAsset) handleAssetCardPreview(item);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          trackAssetLibraryCardClick(item);
                          if (batchMode) handleBatchItemToggle(item);
                          else if (canClickPreviewAsset) handleAssetCardPreview(item);
                        }
                      }}
                    >
                      <div className={styles.mediaLibraryAssetPreview}>
                        {isDocumentCardLayout(item.cardVariant, item.materialType) ? (
                          <MediaLibraryDocumentPreview item={item} />
                        ) : (
                          <AssetModalMediaCover
                            item={item}
                            coverVoiceArtSrc={voiceArtSrc}
                            showAudioWaveform
                            preserveImageAspectRatio
                            audioPlayingId={mediaLibraryAudioPlayingId}
                            audioProgress={mediaLibraryAudioProgress}
                            onAudioSeek={handleMediaLibraryAudioSeek}
                          />
                        )}
                        {!batchMode && (
                          <button
                            type="button"
                            className={styles.mediaLibraryAssetMoreBtn}
                            aria-label={languageStore.smartcatTranslationText((v) => v.roboneo_web_more)}
                            onClick={(e) => {
                              e.stopPropagation();
                              openItemActionMenu(item, e.currentTarget);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                openItemActionMenu(item, e.currentTarget);
                              }
                            }}
                          >
                            <IconFont name="ellipsisFill" fontSize={14} color="#fff" />
                          </button>
                        )}
                        {showInsertHoverBadge && (
                          <div className={styles.mediaLibraryAssetHoverActions}>
                            <button
                              type="button"
                              className={styles.mediaLibraryAssetAddPill}
                              onClick={(e) => {
                                blurHoverActionButtonAfterMouseClick(e);
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddToCanvas(item);
                              }}
                            >
                              {insertHint}
                            </button>
                            {showPreviewPlayBadge && (
                              <button
                                type="button"
                                className={styles.mediaLibraryAssetPlayBtn}
                                aria-label={
                                  previewPlaying
                                    ? languageStore.smartcatTranslationText((v) => v.roboneo_pause)
                                    : languageStore.smartcatTranslationText((v) => v.roboneo_play)
                                }
                                onClick={(e) => {
                                  blurHoverActionButtonAfterMouseClick(e);
                                  if (item.materialType === 'audio') {
                                    handleMediaLibraryAudioPreviewToggle(e, item);
                                    return;
                                  }
                                  handleMediaLibraryVideoPreviewOpen(e, item);
                                }}
                              >
                                <IconFont name={previewPlaying ? 'pauseFill' : 'playFill'} fontSize={12} color="#fff" />
                              </button>
                            )}
                          </div>
                        )}
                        {batchMode && !batchIneligible && (
                          <div
                            className={cn(styles.checkbox, styles.assetSelectCheckbox, {
                              [styles.checked]: isCardSelected,
                              [styles.assetSelectCheckboxDisabled]: batchSelectionLimitDisabled,
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!batchSelectionLimitDisabled) handleBatchItemToggle(item);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !batchSelectionLimitDisabled) {
                                e.stopPropagation();
                                handleBatchItemToggle(item);
                              }
                            }}
                            role="presentation"
                          >
                            {isCardSelected && <IconFont name="checkBoxMark" fontSize={10} color="#fff" />}
                          </div>
                        )}
                      </div>
                      <div className={styles.mediaLibraryCardText}>
                        <div className={styles.mediaLibraryCardTitle} title={title}>
                          {title}
                        </div>
                        <div className={styles.mediaLibraryCardMeta}>
                          <span title={typeLabel}>{typeLabel}</span>
                          {formatLabel && <span title={formatLabel}>{formatLabel}</span>}
                        </div>
                      </div>
                    </AssetModalItemRoot>
                  );
                })}
              </div>
              )}
              {!loading && loadingMore && (
              <div className={styles.loadingMoreWrap}>
                {languageStore.smartcatTranslationText((v) => v.asset_load_more)}
              </div>
              )}
            </div>

            {batchMode && (
            <div
              className={cn(styles.batchBarOuter, {
                [styles.mediaLibraryBatchBarOuter]: mediaLibraryManageBatchMode,
                [styles.subjectPickerAssetLibraryBatchBarOuter]: isSubjectLibraryPicker,
              })}
            >
              {isSubjectLibraryPicker && (
                <SubjectPickerBatchFooter
                  confirmLabel={batchPrimaryLabel}
                  maxSelectableCount={subjectPickerMaxSelectableCount ?? 0}
                  selectedCount={selectedIds.size}
                  variant="assetLibrary"
                  onConfirm={handleBatchAdd}
                />
              )}
              <div
                className={cn(styles.batchBar, {
                  [styles.mediaLibraryBatchBar]: mediaLibraryManageBatchMode,
                })}
                role="toolbar"
                aria-label={languageStore.smartcatTranslationText((v) => v.batch_selection)}
                hidden={isSubjectLibraryPicker}
              >
                <button
                  type="button"
                  className={cn(styles.batchSelectAllBtn, {
                    [styles.mediaLibraryBatchSelectAllBtn]: mediaLibraryManageBatchMode,
                  })}
                  onClick={toggleSelectAllVisible}
                  disabled={batchSelectScopeCount === 0}
                  aria-label={languageStore
                    .smartcatTranslationText((v) => v.select_all_8_selected_)
                    .replace('{N}', String(selectedIds.size))}
                >
                  <span
                    className={cn(styles.batchSelectAllCheck, {
                      [styles.batchSelectAllCheckEmpty]: selectedIds.size === 0,
                      [styles.batchSelectAllCheckIndeterminate]: someVisibleSelected,
                      [styles.batchSelectAllCheckAll]: allVisibleSelected,
                    })}
                  >
                    {someVisibleSelected && (
                      <span className={styles.batchSelectAllCheckIcon}>
                        <IconFont name="minusFill" fontSize={10} color="#fff" aria-hidden />
                      </span>
                    )}
                    {allVisibleSelected && (
                      <span className={styles.batchSelectAllCheckIcon}>
                        <IconFont name="checkBoxMark" fontSize={10} color="#fff" aria-hidden />
                      </span>
                    )}
                  </span>
                  <span className={styles.batchSelectAllLabel}>
                    {languageStore
                      .smartcatTranslationText((v) => v.select_all_8_selected_)
                      .replace('{N}', String(selectedIds.size))}
                  </span>
                </button>
                <div className={styles.batchBarRight}>
                  {mediaLibraryManageBatchMode && (
                    <button
                      type="button"
                      className={cn(styles.batchCancelSelectBtn, styles.mediaLibraryBatchActionBtn)}
                      onClick={handleBatchAdd}
                      disabled={selectedIds.size === 0}
                    >
                      {batchPrimaryLabel}
                    </button>
                  )}
                  <button
                    type="button"
                    className={cn(styles.batchCancelSelectBtn, {
                      [styles.mediaLibraryBatchActionBtn]: mediaLibraryManageBatchMode,
                    })}
                    onClick={clearBatchSelectionOnly}
                    disabled={selectedIds.size === 0}
                  >
                    {languageStore.smartcatTranslationText((v) => v.asset_unselect)}
                  </button>
                  {mediaLibraryManageBatchMode ? (
                    <>
                      <button
                        type="button"
                        className={cn(
                          styles.batchDangerBtn,
                          styles.mediaLibraryBatchActionBtn,
                          styles.mediaLibraryBatchDangerBtn,
                        )}
                        title={!canDelete ? permissionDeniedText : undefined}
                        onClick={openBatchDeleteMediaLibraryItemsConfirm}
                        disabled={selectedIds.size === 0 || !canDelete}
                      >
                        {languageStore.smartcatTranslationText((v) => v['delete-'])}
                      </button>
                      <button
                        type="button"
                        className={cn(
                          styles.batchConfirmBtn,
                          styles.mediaLibraryBatchActionBtn,
                          styles.mediaLibraryBatchPrimaryBtn,
                        )}
                        onClick={openBatchMoveMediaLibraryItemsDialog}
                        disabled={selectedIds.size === 0}
                      >
                        {languageStore.smartcatTranslationText((v) => v.move_to)}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className={styles.batchConfirmBtn}
                      onClick={handleBatchAdd}
                      disabled={selectedIds.size === 0}
                    >
                      {batchPrimaryLabel}
                    </button>
                  )}
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </div>
      {itemActionMenu
        && typeof document !== 'undefined'
        && createPortal(
          <div
            ref={itemActionMenuRef}
            className={cn(styles.mediaLibraryActionMenu, styles.mediaLibraryItemActionMenu)}
            style={{ left: itemActionMenu.left, top: itemActionMenu.top }}
            role="menu"
          >
            {itemActionRoleEditContext && (
              <button
                type="button"
                className={styles.mediaLibraryActionMenuItem}
                onClick={() => handleReEditRoleAsset(itemActionRoleEditContext)}
              >
                <IconFont name="penSquareBold" fontSize={16} color="currentColor" />
                <span>{languageStore.smartcatTranslationText((v) => v.re_edit)}</span>
              </button>
            )}
            <button
              type="button"
              className={styles.mediaLibraryActionMenuItem}
              onClick={() => handleMediaLibraryItemMenuPreview(itemActionMenu.item)}
            >
              <IconFont name="eyeBold" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_card_preview)}</span>
            </button>
            <button
              type="button"
              className={styles.mediaLibraryActionMenuItem}
              onClick={() => openRenameMediaLibraryItemDialog(itemActionMenu.item)}
            >
              <IconFont name="penBold" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.rename_folder)}</span>
            </button>
            <button
              type="button"
              className={styles.mediaLibraryActionMenuItem}
              onClick={() => openMoveSingleMediaLibraryItemDialog(itemActionMenu.item)}
            >
              <IconFont name="arrowRightBrackets" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.move_to)}</span>
            </button>
            <button
              type="button"
              className={styles.mediaLibraryActionMenuItem}
              disabled={!canDownload}
              title={!canDownload ? permissionDeniedText : undefined}
              onClick={() => {
                handleAssetItemDownload(itemActionMenu.item);
              }}
            >
              <IconFont name="downloadBold" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.download)}</span>
            </button>
            <button
              type="button"
              className={cn(styles.mediaLibraryActionMenuItem, styles.danger)}
              disabled={!canDelete}
              title={!canDelete ? permissionDeniedText : undefined}
              onClick={() => openDeleteMediaLibraryItemConfirm(itemActionMenu.item)}
            >
              <IconFont name="trashCanBold" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v['delete-'])}</span>
            </button>
          </div>,
          document.body,
        )}
      {folderActionMenu && !isGenRoleMediaLibraryFolder(folderActionMenu.folder) && (
        <div
          ref={folderActionMenuRef}
          className={cn(styles.mediaLibraryActionMenu, styles.mediaLibraryFolderActionMenu)}
          style={{ left: folderActionMenu.left, top: folderActionMenu.top }}
          role="menu"
        >
          <button
            type="button"
            className={styles.mediaLibraryActionMenuItem}
            disabled={Number(folderActionMenu.folder.depth) >= 10}
            title={Number(folderActionMenu.folder.depth) >= 10 ? mediaLibraryFolderDepthLimitText : undefined}
            onClick={() => {
              openCreateMediaLibraryFolderDialogAtParent(folderActionMenu.folder);
              setFolderActionMenu(null);
            }}
          >
            <IconFont name="plusBold" fontSize={16} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((v) => v.new_folder)}</span>
          </button>
          <button
            type="button"
            className={styles.mediaLibraryActionMenuItem}
            onClick={() => {
              openRenameMediaLibraryFolderDialog(folderActionMenu.folder);
              setFolderActionMenu(null);
            }}
          >
            <IconFont name="penBold" fontSize={16} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((v) => v.rename_folder)}</span>
          </button>
          <button
            type="button"
            className={styles.mediaLibraryActionMenuItem}
            disabled={!folderActionMoveState.canMoveUp}
            onClick={() => handleMoveMediaLibraryFolderWithinParent(folderActionMenu.folder, 'up')}
          >
            <IconFont name="arrowUpBold" fontSize={16} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((v) => v.move_up_one_layer)}</span>
          </button>
          <button
            type="button"
            className={styles.mediaLibraryActionMenuItem}
            disabled={!folderActionMoveState.canMoveDown}
            onClick={() => handleMoveMediaLibraryFolderWithinParent(folderActionMenu.folder, 'down')}
          >
            <IconFont name="arrowDownBold" fontSize={16} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((v) => v.move_down_one_layer)}</span>
          </button>
          <button
            type="button"
            className={styles.mediaLibraryActionMenuItem}
            disabled={Boolean(folderActionMoveDisabledReason)}
            title={folderActionMoveDisabledReason}
            onClick={() => openMoveMediaLibraryFolderDialog(folderActionMenu.folder)}
          >
            <IconFont name="arrowRightBrackets" fontSize={16} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((v) => v.move_to)}</span>
          </button>
          <button
            type="button"
            className={cn(styles.mediaLibraryActionMenuItem, styles.danger)}
            disabled={!canDelete}
            title={!canDelete ? permissionDeniedText : undefined}
            onClick={() => {
              openDeleteMediaLibraryFolderConfirm(folderActionMenu.folder);
              setFolderActionMenu(null);
            }}
          >
            <IconFont name="trashCanBold" fontSize={16} color="currentColor" />
            <span>{languageStore.smartcatTranslationText((v) => v['delete-'])}</span>
          </button>
        </div>
      )}
      <AssetModalNameDialog
        visible={folderNameDialogOpen}
        title={
          folderNameTarget
            ? languageStore.smartcatTranslationText((v) => v.rename_folder)
            : languageStore.smartcatTranslationText((v) => v.new_folder)
        }
        label={languageStore.smartcatTranslationText((v) => v.folder)}
        value={folderNameDraft}
        placeholder={
          folderNameTarget
            ? undefined
            : languageStore.smartcatTranslationText((v) => v.Please_enter_the_folder_name)
        }
        submitLabel={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
        onChange={setFolderNameDraft}
        onClose={() => setFolderNameDialogOpen(false)}
        onSubmit={handleSubmitMediaLibraryFolderName}
      />
      <AssetModalNameDialog
        visible={itemNameDialogOpen}
        title={languageStore.smartcatTranslationText((v) => v.rename_folder)}
        label={languageStore.smartcatTranslationText((v) => v.asset_name)}
        value={itemNameDraft}
        submitLabel={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
        onChange={setItemNameDraft}
        onClose={() => setItemNameDialogOpen(false)}
        onSubmit={handleSubmitMediaLibraryItemName}
      />
      <AssetModalConfirmDialog
        visible={deleteConfirmState != null}
        title={deleteConfirmContent.title}
        note={deleteConfirmContent.note}
        confirmLabel={deleteConfirmContent.confirmLabel}
        onClose={() => setDeleteConfirmState(null)}
        onConfirm={handleConfirmAssetDelete}
      />
      <AssetModalMoveDialog
        visible={moveDialogOpen}
        folders={foldersOrdered}
        targetFolderId={moveTargetFolderId}
        disabledFolderReasons={moveFolderDisabledReasons}
        onTargetFolderChange={setMoveTargetFolderId}
        onClose={() => {
          setMoveDialogOpen(false);
          setMoveFolderTarget(null);
          setMoveItemIds([]);
          setMoveTargetFolderId(undefined);
        }}
        onSubmit={handleSubmitMediaLibraryMove}
      />
      <AssetStorageLimitModal
        open={assetStorageLimitModalOpen}
        onClose={() => setAssetStorageLimitModalOpen(false)}
        onAction={handleAssetStorageLimitAction}
      />
      <CreateRoleModal
        open={createRoleModalOpen}
        insertTarget={insertTarget}
        initialEditContext={createRoleEditContext}
        onClose={() => {
          setCreateRoleModalOpen(false);
          setCreateRoleEditContext(null);
        }}
        onSaveImageAsAsset={handleOpenCreateRoleSaveDialog}
        onAddImageToCanvas={insertTarget === 'canvas' || insertTarget === 'agentTeam'
          ? handleAddCreateRoleImageToCanvas
          : undefined}
        addToCanvasDisabledReason={createRoleAddToCanvasDisabledReason}
      />
      <FeedbackModal
        userInfo={userInfo}
        env={getAssetStorageFeedbackEnv()}
        client_id={getEnv().CLIENT_ID}
        gid={getGid()}
        channel=""
        language={languageStore.languageCode}
        open={assetStorageFeedbackOpen}
        onClose={() => setAssetStorageFeedbackOpen(false)}
        getAccessToken={getAccessToken}
      />

      {selectedFolderId != null && (
        <PersonalAssetImportDialog
          visible={isTeamAssetLibrary && personalAssetImportOpen}
          teamOid={Number(teamOid ?? 0)}
          targetFolderId={selectedFolderId}
          onClose={() => setPersonalAssetImportOpen(false)}
          onImported={handlePersonalAssetImportComplete}
        />
      )}

      {historyUploadPickerOpen
        && typeof document !== 'undefined'
        && createPortal(
          // 历史选择器挂到 body：避免嵌在外层 .mediaLibraryPanel 内被其后代样式（如 .panelMain flex:0 0 732px）污染底部确认栏布局。
          <div className={styles.mediaLibraryHistoryPickerOverlay} role="dialog" aria-modal="true">
            <button
              type="button"
              className={styles.mediaLibraryHistoryPickerBackdrop}
              aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
              onClick={() => {
                if (!mediaLibraryUploading) setHistoryUploadPickerOpen(false);
              }}
              tabIndex={-1}
            />
            <div className={styles.mediaLibraryHistoryPickerDialog}>
              <HistoryModalContent
                setOpenAssetPopover={(open) => {
                  if (!open && !mediaLibraryUploading) {
                    setHistoryUploadPickerOpen(false);
                  }
                }}
                insertTarget={insertTarget}
                enableUploadSuccessTrack={false}
                enableAddCanvasClickTrack={false}
                closeOnBatchConfirm={false}
                defaultBatchMode
                onAssetItemClick={(item) => handleMediaLibraryHistoryUploadItems([item])}
                onAssetBatchConfirm={handleMediaLibraryHistoryUploadItems}
                batchConfirmLabel={languageStore.smartcatTranslationText((v) => v.add_to_assets)}
                assetActionLabel={languageStore.smartcatTranslationText((v) => v.add_to_assets)}
                allowedMaterialTypes={MEDIA_LIBRARY_HISTORY_UPLOAD_ALLOWED_TYPES}
              />
            </div>
          </div>,
          document.body,
        )}
    </AssetLibraryPanelShell>
  );
}

/** 资产库弹窗入口，负责个人/团队/官方资产 tab 编排与官方资产骨架挂载。 */
function AssetLibraryModalContent({
  enableIpCategory,
  browseIpCategory,
  ipCategoryContent,
  setOpenAssetPopover,
  pickerSource,
  maxSelectableCount,
  insertTarget = 'canvas',
  trackLocation,
  officialAssetTrackLocation,
  enableUploadSuccessTrack = true,
  enableAddCanvasClickTrack = true,
  closeOnBatchConfirm = true,
  defaultBatchMode = false,
  onInsertWorkflowMedia,
  onAssetItemClick,
  onAssetBatchItemClick,
  onAssetBatchConfirm,
  batchConfirmLabel,
  assetActionLabel,
  allowedMaterialTypes,
  onIpItemsAdd,
  maxIpSelectableCount,
  ipAddLabel,
  hideIpMaterials,
  hideIpDetail,
}: AssetLibraryModalContentProps): React.ReactElement {
  const session = useMyIpSession();
  const returnHost = useContext(MyIpReturnHostContext);
  const restoredTab = returnHost?.restored?.scopeKey === session.scopeKey ? returnHost.restored.state.assetType : undefined;
  const languageStore = useLanguageStore((s) => s.languageStore);
  const teamOid = useOfficialStore((s) => s.clientOId);
  const isTeamAssetLibrary = teamOid != null && teamOid > 0;
  const {
    activeTab,
    visibleTabs,
    setActiveTab,
  } = useAssetLibraryTab(isTeamAssetLibrary, {
    disableOfficialTab: pickerSource === 'subjectLibrary' && !browseIpCategory,
    initialTab: restoredTab,
  });
  const panelNavigation: AssetLibraryPanelNavigation = {
    activeTab,
    tabs: visibleTabs,
    title: languageStore.smartcatTranslationText((v) => v.asset_library),
    closeLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close),
    onTabChange: setActiveTab,
  };

  if (activeTab === 'official') {
    return (
      <AssetLibraryPanelShell
        activeTab={panelNavigation.activeTab}
        tabs={panelNavigation.tabs}
        title={panelNavigation.title}
        closeLabel={panelNavigation.closeLabel}
        onTabChange={panelNavigation.onTabChange}
        setOpenAssetPopover={setOpenAssetPopover}
      >
        <div className={styles.panelBody}>
          <OfficialAssetContent
            enableIpCategory={canShowMyIpAssetCategory({
              enableIpCategory,
              browseIpCategory,
              pickerSource,
              allowedMaterialTypes,
              onAssetItemClick,
              onAssetBatchConfirm,
              onAssetBatchItemClick,
            })}
            setOpenAssetPopover={setOpenAssetPopover}
            insertTarget={insertTarget}
            officialAssetTrackLocation={officialAssetTrackLocation}
            closeOnBatchConfirm={closeOnBatchConfirm}
            onAssetBatchConfirm={onAssetBatchConfirm}
            maxSelectableCount={maxSelectableCount}
            onIpItemsAdd={onIpItemsAdd}
            maxIpSelectableCount={maxIpSelectableCount}
            addLabel={ipAddLabel ?? assetActionLabel}
            hideMaterials={hideIpMaterials}
            hideIpDetail={hideIpDetail}
          />
        </div>
      </AssetLibraryPanelShell>
    );
  }

  return (
    <AssetLibraryPersonalTeamContent
      enableIpCategory={enableIpCategory}
      browseIpCategory={browseIpCategory}
      ipCategoryContent={ipCategoryContent}
      setOpenAssetPopover={setOpenAssetPopover}
      pickerSource={pickerSource}
      maxSelectableCount={maxSelectableCount}
      insertTarget={insertTarget}
      trackLocation={trackLocation}
      enableUploadSuccessTrack={enableUploadSuccessTrack}
      enableAddCanvasClickTrack={enableAddCanvasClickTrack}
      closeOnBatchConfirm={closeOnBatchConfirm}
      defaultBatchMode={defaultBatchMode}
      onInsertWorkflowMedia={onInsertWorkflowMedia}
      onAssetItemClick={onAssetItemClick}
      onAssetBatchItemClick={onAssetBatchItemClick}
      onAssetBatchConfirm={onAssetBatchConfirm}
      batchConfirmLabel={batchConfirmLabel}
      assetActionLabel={assetActionLabel}
      allowedMaterialTypes={allowedMaterialTypes}
      onIpItemsAdd={onIpItemsAdd}
      maxIpSelectableCount={maxIpSelectableCount}
      ipAddLabel={ipAddLabel}
      hideIpMaterials={hideIpMaterials}
      hideIpDetail={hideIpDetail}
      panelNavigation={panelNavigation}
    />
  );
}

export default AssetLibraryModalContent;
